/**
 * Perkie Approval — post-purchase approval experience (port step 4.75;
 * return-visit pass D13 = port step 18, 2026-09-08).
 *
 * Spec: redesign-brief/money-flows/15-approval-experience-spec.md (§11 lock
 * + §12 amended strings) and 17-account-consent-spec.md (§11 — this page is
 * the guest consent home). Canon artifact: canon-export-2026-08-09/
 * Approval Experience.dc.html (D13, v1788916550420152). Engine contract
 * (spec 15 §2) is preserved: same mount, same order-id sources, GET
 * /approval, POST /approve, POST /feedback, plus the consent pair
 * (GET/POST /sample-consent). POST /notify-optin is NO LONGER CALLED —
 * the SMS block was cut at D13 (no SMS sender exists; ledger 2026-09-08).
 *
 * D13 return-visit facts: the approved state carries ONE dated status line
 * from `approvedAt`; the changes-requested state is dated by `feedbackAt`
 * when the API sends it and runs UNDATED when the key is null or absent
 * (the field ships in perkie-api migration 016 — feature-detected here so
 * the theme is safe to promote before or after that deploy). No other
 * datum is rendered: no fulfilment beat, no tracking number, no delivery
 * date. changes_requested is terminal in the API — nothing on this page
 * implies a revised proof will appear here.
 *
 * Copy law: never "AI"; "pet parents"; Artist-Reviewed hierarchy; memorial
 * register is suppression (no celebration, no deadline, no share, no
 * consent ask); "the next working day", never "today"; delivery is
 * approval-anchored, never a date; no countdowns anywhere; no SMS or
 * nudge claims anywhere on this page (no sender exists).
 *
 * Port step 29 (D20, 2026-09-23 — canon v1790132909500959, fix passes 1
 * and 2): the print-ready file module (offer / preparing / owned / failed,
 * multi-piece button + row links, the memorial "Included" row), the free
 * memorial file, the four shipped states (approvalSource + fulfilledAt;
 * consent dropped for shipped pieces; share never shows a memorial pet),
 * the digital-only approved state, and the checkout → Preparing return.
 * Every file fact comes from the API's `digitalFile` / `digitalOnly`; the
 * page never infers ownership, and absent facts render nothing new. The
 * download is a plain link to the API's owner-only endpoint.
 *
 * Port step 31 (D20-B, 2026-09-24 — canon v1790276495838125, fix pass 1):
 * the states step 29 withheld. The multi-piece module with nothing to buy
 * (owned / included rows still reachable; the memorial-only form when the
 * offer is off), the checkout-opened and unmeasured rows, the digital-only
 * order settled by fulfilment (COMPLETE), the shipped piece whose file
 * failed, the multi checkout-start failure card, approved + master still
 * building (the offer renders whenever the API sends checkoutUrl; the size
 * line is withheld until maxPrintSize exists), the combined shipped
 * headline, the digital-only pending register (no print framing), the
 * partial-order beat with its memorial branch, and the unported canon:
 * badge, rail tiles + chip, print lines, the 9:16 card heading. Rulings
 * R1 (the day-7 line is a person's act; memorial exempt) and R2(a) (on a
 * mixed order the memorial file line sits above the consent block, which
 * stays last, ruled off by a hairline and 30px). Still only API facts.
 *
 * Port step 54 (SX-7(3), ruled Corey 2026-10-27, ledger CoS-4): shipped
 * wins over changes requested once fulfilledAt is set. The API stamps
 * fulfilled_at on a changes_requested item of a fully fulfilled order and
 * changes nothing else (status, approvalSource and approvedAt stay as
 * they are), so such a piece takes the shipped state, which never prints
 * an approval or its date for a piece she did not approve. What is offered
 * only on an approved piece (the share row, the print-ready file module,
 * the consent ask) keeps that rule. Not shipped: the changes state, as
 * before. Digital-only: unchanged (a digital order never ships).
 *
 * No perkie:* events are dispatched or listened for in this file (whole-
 * theme grep verified at build, re-verified step 29 and 31). DOM events
 * only, all on nodes this file creates, plus window pageshow (bfcache).
 */
(function () {
  "use strict";

  var STYLE_NAMES = {
    bw: "Black & White",
    color: "Color",
    ink: "Ink Wash",
    marker: "Marker",
  };
  var POLL_MS = 5000;
  var POLL_MAX_MS = 4 * 60 * 1000; // stop polling after 4 minutes
  var NOT_FOUND_RETRY_MS = 30000; // webhook-lag window on first load

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function reducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function fmtDate(iso) {
    if (!iso) return "";
    try {
      var d = new Date(iso);
      return d.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return "";
    }
  }

  // D13 status rows read "Approved September 4" / "Changes requested
  // September 6" — month and day, the way the artifact draws them. The
  // year stays off: these lines answer "did it register?", not archive.
  function fmtDay(iso) {
    if (!iso) return "";
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
    } catch (e) {
      return "";
    }
  }

  // The feedback sheet opens with the caret ready on a fine pointer; on a
  // coarse (touch) pointer a programmatic focus summons the keyboard and
  // scrolls the sheet out from under the customer, so it is skipped.
  function coarsePointer() {
    return (
      window.matchMedia && window.matchMedia("(pointer: coarse)").matches
    );
  }

  function styleLabel(style) {
    return STYLE_NAMES[style] || style || "";
  }

  function init(root) {
    // In-file double-load guard (standing rule): scoped to the mount node
    // this file owns, never a shared event, global or DOM namespace.
    if (root.getAttribute("data-pa-init") === "1") return;
    root.setAttribute("data-pa-init", "1");
    var apiUrl = (root.getAttribute("data-api-url") || "").replace(/\/$/, "");
    var context = root.getAttribute("data-context") || "page";
    var resendEnabled = root.getAttribute("data-resend") === "true";
    // markUrl is currently unused: the chrome bar's nose mark was dropped
    // (repair 31-a, Corey ruling 2026-09-24). Kept with the data-mark
    // attribute in sections/perkie-approval.liquid in case the mark returns.
    var markUrl = root.getAttribute("data-mark") || "";

    // Order id comes from the template (account order page) or from the
    // ?perkie_order= query param (approval page — Liquid cannot read query
    // params, so this must happen client-side). URL CONTRACT — unchanged.
    var orderId = root.getAttribute("data-order-id") || "";
    if (!orderId) {
      try {
        orderId =
          new URLSearchParams(window.location.search).get("perkie_order") || "";
      } catch (e) {
        orderId = "";
      }
    }

    // Step 29 return path: the thank-you page's "Back to {Name}'s piece"
    // carries ?perkie_piece=<artwork id> so the page lands on that piece.
    // Applied once, on the first load that contains it.
    var landingPiece = "";
    try {
      landingPiece =
        new URLSearchParams(window.location.search).get("perkie_piece") || "";
    } catch (e) {
      landingPiece = "";
    }

    if (!apiUrl) return;

    function track(action, params) {
      if (typeof gtag === "function") {
        var p = params || {};
        p.order_id = orderId;
        gtag("event", action, p);
      }
    }

    // ------------------------------------------------------------------
    // No-order-id arrival (page context): coach warmly, never blank.
    // ------------------------------------------------------------------
    if (!orderId) {
      if (context === "page") {
        root.textContent = "";
        appendNoOrderComposition(root);
        root.hidden = false;
      }
      return;
    }

    // Canon no-order composition (delta ruling 10, 2026-08-24): a warm
    // coach with two routes back in — email first, account second,
    // "because the email link is the one most people still have". Shared
    // by the no-order-id arrival and the dead-link 404 (page context).
    function appendNoOrderComposition(container) {
      var plate = el("div", "pa-plate");
      plate.appendChild(
        el("span", "pa-plate__label", "NO ORDER ON THIS LINK")
      );
      container.appendChild(plate);
      container.appendChild(
        el("h2", "pa-heading", "This link is not pointing at an order")
      );
      container.appendChild(
        el(
          "p",
          "pa-sub",
          "Proof links are personal and they expire. Two ways back in, both quick."
        )
      );
      var route1 = el("div", "pa-fact");
      route1.appendChild(
        el("strong", "pa-fact__title", "Open the newest email from us")
      );
      route1.appendChild(
        document.createTextNode(
          "Subject line starts with “Your proof is ready”. The button in that email always carries the live link."
        )
      );
      container.appendChild(route1);
      var route2 = el("div", "pa-fact");
      route2.appendChild(
        el("strong", "pa-fact__title", "Or sign in and pick the order")
      );
      route2.appendChild(
        document.createTextNode(
          "Every proof for every pet in your account sits under Orders, waiting for you."
        )
      );
      container.appendChild(route2);
      var ordersLink = el("a", "pa-btn pa-btn--outline", "Go to my orders");
      ordersLink.href = "/account";
      container.appendChild(ordersLink);
      if (resendEnabled) container.appendChild(buildResendForm());
    }

    function buildResendForm() {
      var wrap = el("div", "pa-resend");
      var orderField = el("input", "pa-resend__field");
      orderField.type = "text";
      orderField.placeholder = "Order number (like #37138)";
      orderField.autocomplete = "off";
      var emailField = el("input", "pa-resend__field");
      emailField.type = "email";
      emailField.placeholder = "Email you used at checkout";
      emailField.autocomplete = "email";
      var btn = el("button", "pa-btn pa-btn--primary", "Email me my link");
      btn.type = "button";
      var out = el("p", "pa-note", "");
      btn.addEventListener("click", function () {
        var num = orderField.value.trim();
        var email = emailField.value.trim();
        if (!num || !email) {
          out.textContent =
            "Please enter your order number and the email you used at checkout.";
          return;
        }
        btn.disabled = true;
        btn.textContent = "Sending…";
        track("consent_resend_requested", { surface: "approval" });
        fetch(apiUrl + "/api/v2/orders/approval-link/resend", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderNumber: num, email: email }),
        })
          .then(function (res) { return res.json(); })
          .then(function (json) {
            btn.disabled = false;
            btn.textContent = "Email me my link";
            out.textContent =
              json.message ||
              "If those match an order with artwork, the link is on its way to that email.";
            track("consent_resend_result", { surface: "approval", result: "sent" });
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = "Email me my link";
            out.textContent = "Something went wrong on our end. Please try again.";
            track("consent_resend_result", { surface: "approval", result: "error" });
          });
      });
      wrap.appendChild(orderField);
      wrap.appendChild(emailField);
      wrap.appendChild(btn);
      wrap.appendChild(out);
      return wrap;
    }

    // ------------------------------------------------------------------
    // State
    // ------------------------------------------------------------------
    var state = {
      items: [],
      order: { orderNumber: "" },
      consent: null, // ask-state payload, loaded post-approval
      consentBusy: {}, // orderItemId -> true while a write is in flight
      consentErrors: {}, // orderItemId -> message
      withdrawn: {}, // orderItemId -> true after an in-session withdraw
      focus: 0, // index into visible artworks (multi-artwork rail)
      sentFeedback: {}, // itemId -> text sent this session (echo before reload)
      pollStart: Date.now(),
      pollTimer: null,
      loadError: false,
      notFound: false,
      consentShownTracked: false,
      // D13: the page state last painted, so approval_state_shown fires
      // once per state (not once per poll) and the entrance motion runs
      // only when the state actually changes.
      shownState: null,
      shownAt: 0, // when shownState last changed (entrance window)
      enterTimer: null,
      // D13 rider: a background refetch (visibilitychange, poll, consent)
      // must never wipe an open feedback sheet — it defers instead.
      renderDeferred: false,
      // Step 29: order-level fact from the API (D20 fix pass 2 D3).
      digitalOnly: false,
      fileShownTracked: {}, // itemId+kind -> true (print_file_shown once)
      // Step 31: the partial-order beat after a fresh approve on a multi
      // order ({ id, name, remaining, memorial }); null once advanced.
      beat: null,
      beatTimer: null,
      // Step 31: the multi checkout-start failure card — the item ids the
      // failed attempt carried, so the card retries the same set.
      checkoutFailed: null,
    };

    // Whole-page state key for approval_state_shown and the entrance
    // motion. Computed from the focused artwork the way the artifact's
    // simulator names its states.
    function stateKey(focused) {
      if (state.loadError) return "error";
      if (state.notFound) return "no_order";
      if (!focused) return "nothing_ready";
      if (focused.approvalStatus === "approved") {
        if (state.beat && state.beat.id === focused.id) return "approved_beat";
        if (isShipped(focused)) {
          return shippedFullState() ? "shipped" : "shipped_piece";
        }
        if (!allVisibleApproved()) return "approved_piece";
        if (isCompleted(focused)) return "digital_completed";
        return state.digitalOnly ? "digital_only" : "approved";
      }
      // Step 54 (SX-7(3)): shipped wins over changes requested, so it is
      // read BEFORE the changes branch. Only a changes_requested piece can
      // reach this line (approved is handled above).
      if (isShipped(focused)) {
        return shippedFullState() ? "shipped" : "shipped_piece";
      }
      if (focused.approvalStatus === "changes_requested") {
        return "changes_requested";
      }
      if (isGenerating(focused)) return "generating";
      if (!shownUrl(focused)) return "nothing_ready";
      if (focused.hiresStatus === "failed") return "print_failed";
      return state.digitalOnly ? "digital_pending" : "pending";
    }

    // Fires GA4 approval_state_shown{state, memorial} once per distinct
    // state per page life, and toggles the entrance class so the rise
    // motion plays on a state change and never on a same-state repaint.
    function markStateShown(key, memorial) {
      var changed = key !== state.shownState;
      root.classList.toggle("pa--enter", changed);
      if (!changed) return;
      state.shownState = key;
      state.shownAt = Date.now();
      track("approval_state_shown", { state: key, memorial: memorial ? 1 : 0 });
    }

    // The entrance runs --duration-default (.32s) plus the longest
    // stagger (160ms); a background repaint inside that window would
    // rebuild the nodes mid-rise and cut the motion short.
    var ENTER_MS = 520;

    // Background refreshes route through here: if the customer has the
    // feedback sheet open, the repaint waits until the sheet closes; if
    // an entrance is still playing, it waits for that to finish.
    function renderUnlessSheetOpen() {
      if (root.querySelector(".pa-form")) {
        state.renderDeferred = true;
        return;
      }
      var sinceEnter = Date.now() - (state.shownAt || 0);
      if (sinceEnter < ENTER_MS && !reducedMotion()) {
        clearTimeout(state.enterTimer);
        state.enterTimer = setTimeout(renderUnlessSheetOpen, ENTER_MS - sinceEnter);
        return;
      }
      render();
    }

    function flushDeferredRender() {
      if (!state.renderDeferred) return;
      state.renderDeferred = false;
      render();
    }

    function endpoint(path) {
      return apiUrl + "/api/v2/orders/" + encodeURIComponent(orderId) + path;
    }

    function fetchStatus() {
      return fetch(endpoint("/approval")).then(function (res) {
        if (res.status === 404) {
          var err = new Error("not_found");
          err.notFound = true;
          throw err;
        }
        if (!res.ok) throw new Error("status " + res.status);
        return res.json();
      });
    }

    function post(path, body) {
      return fetch(endpoint(path), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) {
            var err = new Error(json.error || "Request failed");
            err.status = res.status;
            throw err;
          }
          return json;
        });
      });
    }

    // ------------------------------------------------------------------
    // Item semantics
    // ------------------------------------------------------------------
    function isCombined(item) {
      // composition ships with the 4.75 API additions; pet_number 0 is the
      // migration-010 convention and covers the window before that deploy.
      return (
        item.composition === "combined" ||
        item.composition === "fallback" ||
        item.petNumber === 0
      );
    }

    function itemMemorial(item) {
      return item.memorial === true; // absent (pre-4.75 API) => false
    }

    // Port step 26 (ruled 2026-09-22): the page NEVER reads the print
    // master. displayUrl is a ≤2048px render of it (API migration 017);
    // absent on the pre-26 API and on orders produced before it, where the
    // preview is shown — so this is safe to promote before the API deploys.
    function shownUrl(item) {
      return item.displayUrl || item.previewUrl || "";
    }

    function isVisible(item) {
      // Items with no production image and no preview have nothing to
      // show — they follow the standard email proof flow (Lane 3).
      return item.hiresStatus !== "pending" || Boolean(item.previewUrl);
    }

    function isGenerating(item) {
      return (
        item.hiresStatus === "generating" || item.hiresStatus === "pending"
      );
    }

    function visibleItems() {
      return state.items.filter(isVisible);
    }

    function memorialOrder() {
      return state.items.some(itemMemorial);
    }

    function anyApproved() {
      return state.items.some(function (i) {
        return i.approvalStatus === "approved";
      });
    }

    // The FULL approved state (dated status line, "ships in 1–3 business days after
    // the artwork is approved", the four facts) is withheld until every
    // visible piece is approved. The artifact withholds it while any piece
    // is still pending; this build also withholds it while a sibling piece
    // has changes requested, because that piece's own state says "nothing
    // prints until you approve" and the two lines cannot both be true on
    // one order (artifact gap, reported at step 18).
    function allVisibleApproved() {
      var visible = visibleItems();
      return (
        visible.length > 0 &&
        visible.every(function (i) {
          return i.approvalStatus === "approved";
        })
      );
    }

    // Next piece still needing the customer (not approved), after `item`
    // in rail order, wrapping. Null when there is none. Step 54: a shipped
    // piece needs nothing from her, whatever its status.
    function nextOpenItem(item) {
      var visible = visibleItems();
      var start = visible.indexOf(item);
      for (var k = 1; k <= visible.length; k++) {
        var cand = visible[(start + k) % visible.length];
        if (cand !== item && cand.approvalStatus !== "approved" && !isShipped(cand)) {
          return cand;
        }
      }
      return null;
    }

    // --- Port step 29 (D20 fix pass 2 D1/D3) ---------------------------
    // Shipped = a piece on an order Shopify fully fulfilled (step 28:
    // fulfilledAt) that is approved or, since step 54 (SX-7(3)), still has
    // changes requested: the API stamps those too and shipped wins. A
    // digital-only order never "ships" — its own state wins.
    function isShipped(item) {
      return (
        (item.approvalStatus === "approved" ||
          item.approvalStatus === "changes_requested") &&
        Boolean(item.fulfilledAt) &&
        !state.digitalOnly
      );
    }

    // Approved by HER: the approve POST ('customer'), or a legacy approval
    // (pre-019, dated, no source — the POST was then the only writer).
    // Step 54: never true for a piece that is not approved (a shipped
    // changes_requested piece prints no "you approved").
    function approvedByCustomer(item) {
      if (item.approvalStatus !== "approved") return false;
      if (item.approvalSource === "customer") return true;
      return !item.approvalSource && Boolean(item.approvedAt);
    }

    // The approved pieces on screen (the artifact's `pool`).
    function approvedPool() {
      return visibleItems().filter(function (i) {
        return i.approvalStatus === "approved";
      });
    }

    // Step 54: the shipped state's pool — the approved pieces plus any
    // shipped piece still marked changes requested, in rail order. It is
    // approvedPool() exactly on every order where no such piece exists. It
    // feeds the shipped headline, sub, ship date and likeness line only;
    // the share row and the file module keep approvedPool().
    function shippedStatePool() {
      return visibleItems().filter(function (i) {
        return i.approvalStatus === "approved" || isShipped(i);
      });
    }

    // Artifact showFullApproved for a shipped piece: every piece is settled
    // (approved, or changes requested — a changes sibling gets its own box).
    function shippedFullState() {
      return visibleItems().every(function (i) {
        return (
          i.approvalStatus === "approved" ||
          i.approvalStatus === "changes_requested"
        );
      });
    }

    // The API's file facts, or null (pre-020 API / nothing to say).
    function fileFacts(item) {
      var d = item && item.digitalFile;
      return d && typeof d === "object" ? d : null;
    }

    function possessive(name) {
      return name + "’s";
    }

    // --- Port step 31 (D20-B) ------------------------------------------
    // Digital-only order settled by fulfilment (D20-B item 4): approved by
    // the orders/fulfilled webhook, nothing shipped. The artifact's
    // `completed`. Legacy rows carry no approvalSource and never take it.
    function isCompleted(item) {
      return (
        item.approvalStatus === "approved" &&
        state.digitalOnly &&
        item.approvalSource === "shipped"
      );
    }

    // Canon print line (artifact printLine), the fact beside the chip.
    // Empty when the page has nothing true to say (pending, no file yet).
    function printLine(item) {
      if (isShipped(item)) return "Printed and shipped";
      if (state.digitalOnly) {
        return item.approvalStatus === "approved"
          ? "Full-size file ready"
          : "Digital order · full-size file";
      }
      if (isGenerating(item)) return "Print file still building";
      if (item.hiresStatus === "complete") return "Ready to print at full quality";
      if (item.hiresStatus === "failed") return "Print file being rebuilt by an artist";
      return "";
    }

    // Canon badge on the figure of an approved piece.
    function badgeLabel(item) {
      if (isShipped(item)) return "SHIPPED";
      if (isCompleted(item)) return "COMPLETE";
      if (state.digitalOnly) return "APPROVED";
      return "APPROVED FOR PRINT";
    }

    // Canon rail tile status + mark.
    function railStatus(item) {
      if (item.approvalStatus === "approved") {
        return {
          text: isShipped(item)
            ? "Shipped"
            : isCompleted(item)
              ? "Complete"
              : state.digitalOnly
                ? "Approved"
                : "Approved for print",
          mark: "✓",
          on: true,
        };
      }
      // Step 54: a shipped changes_requested piece reads as shipped.
      if (isShipped(item)) {
        return { text: "Shipped", mark: "✓", on: true };
      }
      if (item.approvalStatus === "changes_requested") {
        return { text: "Changes requested", mark: "•", on: false };
      }
      return { text: "Waiting on you", mark: "○", on: false };
    }

    // The memorial piece among the approved pieces (artifact memPet).
    function memorialInPool() {
      var pool = approvedPool();
      for (var i = 0; i < pool.length; i++) {
        if (itemMemorial(pool[i])) return pool[i];
      }
      return null;
    }

    // Pieces still needing the customer (artifact !resolvedOf).
    function unresolvedItems() {
      return visibleItems().filter(function (i) {
        return i.approvalStatus === "pending";
      });
    }

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    function render() {
      root.textContent = "";
      state.renderDeferred = false;
      var mem0 = memorialOrder();
      root.classList.toggle("pa--memorial", mem0);
      // Memorial register rides the theme-wide canon system: the modifier
      // collapses accent roles to jet (no violet CTA on a memorial page).
      root.classList.toggle("perkie-canon--memorial", mem0);

      if (state.loadError) {
        markStateShown("error", mem0);
        renderErrorState();
        root.hidden = false;
        return;
      }
      if (state.notFound) {
        // Order context: a 404 means a non-custom order — the widget has
        // no business rendering there. Page context (an explicit link):
        // never a blank screen.
        if (context === "order") {
          root.hidden = true;
          return;
        }
        markStateShown("no_order", mem0);
        renderNotFoundState();
        root.hidden = false;
        return;
      }

      var visible = visibleItems();
      if (state.items.length > 0 && visible.length === 0) {
        // Artwork exists but nothing is showable yet (skip-flow lines):
        // honest wait, never a hidden page.
        markStateShown("nothing_ready", mem0);
        root.appendChild(el("h2", "pa-heading", "Your artwork"));
        root.appendChild(
          el(
            "p",
            "pa-sub",
            "Our artists are preparing your proof — we'll send it by email as soon as it's ready."
          )
        );
        root.hidden = false;
        return;
      }
      if (visible.length === 0) {
        // Nothing loaded yet (first paint before fetch resolves).
        root.hidden = true;
        return;
      }
      root.hidden = false;

      if (state.focus >= visible.length) state.focus = 0;
      var focused = visible[state.focus];
      var mem = memorialOrder();
      markStateShown(stateKey(focused), mem);

      // Canon composition (parity fix 2026-08-24, Appendix B A1/A8): the
      // page opens on the proof with only a small chrome bar above it —
      // no page heading, no intro prose. "The first seconds belong to
      // meeting the piece, not to deciding about it" (canon BET 01). The
      // pet's name renders UNDER the proof, in renderCard.
      var chrome = renderChrome();
      if (chrome) root.appendChild(chrome);

      var anyPending = visible.some(function (i) {
        return i.approvalStatus === "pending" && Boolean(shownUrl(i));
      });

      // D13 wide state (artifact 1200 / 1440 frames): the proof takes its
      // own column and stays with the reader while the facts scroll. The
      // figure is lifted out of the card into the art column; everything
      // else flows in the body column. Below 1200 both columns stack in
      // the same order as before, so 390 is unchanged.
      var layout = el("div", "pa-layout");
      var artCol = el("div", "pa-layout__art");
      var bodyCol = el("div", "pa-layout__body");
      layout.appendChild(artCol);
      layout.appendChild(bodyCol);
      root.appendChild(layout);

      var card = renderCard(focused);
      var figure = card.querySelector(".pa-figure");
      if (figure) artCol.appendChild(figure);
      else artCol.hidden = true;

      // Rail sits UNDER the name block (artifact order: art · name · meta ·
      // chip + print line · rail · notice), one piece in focus at a time.
      if (visible.length > 1) {
        var anchor =
          card.querySelector(".pa-quality") || card.querySelector(".pa-card__style");
        var rail = renderRail(visible);
        var caption = el(
          "p",
          "pa-rail__caption",
          "Each Perkie Print is approved on its own."
        );
        if (anchor) {
          anchor.after(rail);
          rail.after(caption);
        } else {
          card.insertBefore(caption, card.firstChild);
          card.insertBefore(rail, caption);
        }
      }

      bodyCol.appendChild(card);
      bodyCol.appendChild(renderFacts(anyPending, mem, focused));

      // Consent renders STRICTLY LAST (spec 15 §11) and only once an
      // approval exists on the order (the ruled ask moment). R2(a), step
      // 31: on a mixed order the memorial file line sits above it, so the
      // block is ruled off by a hairline and 30px (artifact consentRule).
      if (anyApproved()) {
        var consentBlock = renderConsentArea();
        if (consentBlock) {
          if (memorialInPool()) consentBlock.classList.add("pa-consent--ruled");
          bodyCol.appendChild(consentBlock);
        }
      }

      if (anyPending) {
        bodyCol.appendChild(
          el(
            "p",
            "pa-later",
            "Prefer to decide later? No rush. We'll also send your proof by email."
          )
        );
      }
    }

    // Canon error composition (delta ruling 10, 2026-08-24): what is
    // true, what is safe, what happens next — never a blank screen.
    function renderErrorState() {
      var plate = el("div", "pa-plate");
      plate.appendChild(el("span", "pa-plate__label", "PROOF NOT LOADED"));
      root.appendChild(plate);
      root.appendChild(
        el("h2", "pa-heading", "We could not reach your proof")
      );
      root.appendChild(
        el(
          "p",
          "pa-sub",
          "Your artwork is safe and nothing you do here can lose it. This is on our side, and one more try usually clears it."
        )
      );
      var retry = el("button", "pa-btn pa-btn--primary", "Try again");
      retry.type = "button";
      retry.addEventListener("click", function () {
        state.loadError = false;
        state.pollStart = Date.now();
        render();
        load();
      });
      root.appendChild(retry);
      root.appendChild(
        el(
          "p",
          "pa-hand",
          "Still stuck after a couple of tries? Reply to your order email and a person will send the proof straight to you."
        )
      );
    }

    function renderNotFoundState() {
      appendNoOrderComposition(root);
    }

    // Chrome bar (canon element 1): just the order number, right-aligned.
    // The nose mark and the page title (h2) were dropped
    // (repair 31-a, Corey ruling 2026-09-24). Quiet page identity — never
    // a display heading. Renders nothing when there is no order number,
    // so the bar is never empty.
    function renderChrome() {
      var num = state.order.orderNumber;
      if (!num) return null;
      var bar = el("div", "pa-chrome");
      bar.appendChild(
        el(
          "span",
          "pa-chrome__order",
          "Order " + String(num).replace(/^#/, "")
        )
      );
      return bar;
    }

    // Step 31 (canon rail, D29-CANON-UNPORTED): "PIECE n OF m" with the
    // progress chip ("n OF m SHIPPED" once anything shipped, else "n OF m
    // APPROVED"), then one tile per piece — mark, name, status line.
    function renderRail(visible) {
      var wrap = el("div", "pa-rail");
      var head = el("div", "pa-rail__head");
      head.appendChild(
        el("span", "pa-rail__label", "PIECE " + (state.focus + 1) + " OF " + visible.length)
      );
      var shippedCount = visible.filter(isShipped).length;
      var approvedCount = visible.filter(function (i) {
        return i.approvalStatus === "approved";
      }).length;
      head.appendChild(
        el(
          "span",
          "pa-rail__chip",
          shippedCount > 0
            ? shippedCount + " OF " + visible.length + " SHIPPED"
            : approvedCount + " OF " + visible.length + " APPROVED"
        )
      );
      wrap.appendChild(head);

      var stops = el("div", "pa-rail__stops");
      stops.setAttribute("role", "tablist");
      stops.setAttribute("aria-label", "Your artworks");
      visible.forEach(function (item, idx) {
        var st = railStatus(item);
        var stop = el("button", "pa-rail__stop");
        stop.type = "button";
        stop.setAttribute("aria-current", String(idx === state.focus));
        if (st.on) stop.classList.add("pa-rail__stop--on");
        var line = el("span", "pa-rail__line");
        var mark = el("span", "pa-rail__mark", st.mark);
        mark.setAttribute("aria-hidden", "true");
        line.appendChild(mark);
        line.appendChild(
          el("span", "pa-rail__name", item.petName || "Artwork " + (idx + 1))
        );
        stop.appendChild(line);
        stop.appendChild(el("span", "pa-rail__status", st.text));
        stop.addEventListener("click", function () {
          state.focus = idx;
          clearBeat();
          render();
        });
        stops.appendChild(stop);
      });
      wrap.appendChild(stops);
      return wrap;
    }

    function renderCard(item) {
      var card = el("div", "pa-card");
      var mem = itemMemorial(item);

      var url = shownUrl(item);
      var figure = el("div", "pa-figure");

      // Canon composition (parity fix, Appendix B A1): the proof renders
      // FIRST — full width on paper cream, no card edge — and the pet's
      // name sits UNDER it at display weight. Meeting the piece precedes
      // reading about it. When there is no image yet, the name leads.
      function appendNameBlock(withChip) {
        card.appendChild(
          el("h3", "pa-card__pet", item.petName || "Your pet")
        );
        var styleLine = isCombined(item)
          ? "One artwork · " + styleLabel(item.style)
          : styleLabel(item.style);
        card.appendChild(el("p", "pa-card__style", styleLine));
        // Trust row (Appendix B A5 + step 31 canon print lines): the
        // LABEL-tier chip beside the print line, on every state that has a
        // proof to show. Trust hierarchy — "Artist-Reviewed" is the label;
        // the sentence tier never shares a piece with it.
        if (withChip) {
          var qrow = el("div", "pa-quality");
          qrow.appendChild(el("span", "pa-chip-label", "Artist-Reviewed"));
          var pl = printLine(item);
          if (pl) qrow.appendChild(el("span", "pa-quality__text", pl));
          card.appendChild(qrow);
        }
      }

      // Step 31 (D20-B item 7): an APPROVED piece whose master is still
      // being written takes the approved state (print line "Print file
      // still building"; the poll keeps reading) — the spinner card is for
      // a proof that is still being judged. Step 54: never for a shipped
      // piece (shipped wins).
      if (
        isGenerating(item) &&
        item.approvalStatus !== "approved" &&
        !isShipped(item)
      ) {
        if (item.previewUrl) {
          var prev = el("img", "pa-figure__img");
          prev.src = item.previewUrl;
          prev.alt = (item.petName || "Pet") + " preview";
          figure.appendChild(prev);
        }
        var preparing = el("div", "pa-preparing");
        preparing.appendChild(el("span", "pa-spinner"));
        preparing.appendChild(document.createElement("br"));
        preparing.appendChild(
          document.createTextNode(
            "Preparing the print file. This usually takes about a minute."
          )
        );
        figure.appendChild(preparing);
        card.appendChild(figure);
        // Chip + "Print file still building" only when a proof is on screen.
        appendNameBlock(Boolean(item.previewUrl));
        return card;
      }

      if (!url) {
        // No print file and no preview (skip-flow lines): unchanged Lane-3
        // register, no scary error. Nothing to meet yet — the name leads.
        appendNameBlock();
        card.appendChild(
          el(
            "p",
            "pa-note",
            "Our artists are preparing your proof — we'll send it by email as soon as it's ready."
          )
        );
        return card;
      }

      // Status-based (step 26): the print file exists server-side; the page
      // shows its display render (or the preview), never the file itself.
      var hasPrintFile = item.hiresStatus === "complete";

      var img = el("img", "pa-figure__img");
      img.src = url;
      img.alt = (item.petName || "Pet") + " portrait";
      // The proof is the page's LCP element — never lazy-load it. The
      // whole figure is tappable (mobile zoom habit); the pill is the
      // labelled affordance and the accessible control.
      img.addEventListener("click", function () {
        openZoom(item, hasPrintFile);
      });
      figure.appendChild(img);

      // Tap to inspect (Appendix B A4): a pill overlaid ON the image,
      // bottom-right, with an expand glyph — an affordance on the
      // artwork. Serves shownUrl (display render or preview) — step 26.
      var inspect = el("button", "pa-inspect");
      inspect.type = "button";
      // Canon icon (Approval Experience.dc.html, Pending·image state):
      // 16×16 four-corner expand, stroked in the control's own color so
      // the memorial collapse tracks it for free.
      var glyph = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg"
      );
      glyph.setAttribute("class", "pa-inspect__glyph");
      glyph.setAttribute("viewBox", "0 0 24 24");
      glyph.setAttribute("aria-hidden", "true");
      var glyphPath = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path"
      );
      glyphPath.setAttribute("d", "M4 9V4h5M20 15v5h-5M20 9V4h-5M4 15v5h5");
      glyphPath.setAttribute("fill", "none");
      glyphPath.setAttribute("stroke", "currentColor");
      glyphPath.setAttribute("stroke-width", "2");
      glyphPath.setAttribute("stroke-linecap", "round");
      glyph.appendChild(glyphPath);
      inspect.appendChild(glyph);
      inspect.appendChild(document.createTextNode("Tap to inspect"));
      inspect.addEventListener("click", function () {
        openZoom(item, hasPrintFile);
      });
      figure.appendChild(inspect);
      // Step 31 canon badge (D29-CANON-UNPORTED), top-left on the figure of
      // an approved piece: SHIPPED · COMPLETE · APPROVED · APPROVED FOR PRINT.
      // Step 54: a shipped changes_requested piece carries SHIPPED.
      if (item.approvalStatus === "approved" || isShipped(item)) {
        figure.appendChild(el("span", "pa-badge", badgeLabel(item)));
      }
      card.appendChild(figure);
      appendNameBlock(true);

      if (item.approvalStatus === "approved") {
        // Step 31: the partial-order beat right after a fresh approve on a
        // multi order (artifact showBeat) — instead of the approved state.
        if (state.beat && state.beat.id === item.id) {
          renderBeat(item, card);
          return card;
        }
        // D20 state 1 (artifact approvedPrintFailed): approved, then the
        // print file failed — the rebuild notice, verbatim. Not on a
        // shipped piece: "rebuilds it before printing" is past for it.
        if (item.hiresStatus === "failed" && !isShipped(item)) {
          var rebuild = el("div", "pa-fact");
          rebuild.appendChild(
            el("strong", "pa-fact__title", "Print file needs a rebuild on our side")
          );
          rebuild.appendChild(
            document.createTextNode(
              "The proof above is complete and correct. The high-resolution copy failed to write, so an artist rebuilds it before printing. Nothing is lost."
            )
          );
          card.appendChild(rebuild);
        }
        renderApprovedState(item, card);
      } else if (isShipped(item)) {
        // Step 54 (SX-7(3)): a changes_requested piece that shipped takes
        // the shipped state. Checked before the changes branch: shipped
        // wins. renderApprovedState prints no approval for it.
        renderApprovedState(item, card);
      } else if (item.approvalStatus === "changes_requested") {
        renderChangesState(item, card);
      } else {
        // Pending with an image to judge. When the print file failed, the
        // preview is the fidelity-preserving stand-in (engine law — see
        // isApprovable() in perkie-api approval.js). Approval stays
        // OFFERED; only the print-file-literal claims are withheld.
        // (The chip + print line now render in the name block, step 31.)
        if (state.digitalOnly) {
          // Step 31 (D20-B item 9): a digital order is never framed as
          // print — the notice names the file she receives.
          var df = el("div", "pa-fact");
          df.appendChild(
            el("strong", "pa-fact__title", "This is the file you’ll receive")
          );
          df.appendChild(
            document.createTextNode(
              "The proof above is the finished piece. What you approve is the full-size file that comes with your order."
            )
          );
          card.appendChild(df);
        } else if (hasPrintFile) {
          // Canon notice pair (delta ruling 4, 2026-08-24): the verified
          // noticeHead plus canon's body. The size clause ships per the
          // ruling even though size is not rendered on this page
          // (D-APPROVAL-SIZE — API gap, debt elevated by the PM).
          var pf = el("div", "pa-fact");
          pf.appendChild(
            el(
              "strong",
              "pa-fact__title",
              "This is the file that goes to paper"
            )
          );
          pf.appendChild(
            document.createTextNode(
              // D20 fix pass 1 B4 (verbatim): the print-quality claim is
              // dropped — the figure is a display render (step 26).
              "The proof above is the finished piece. What you approve is what we print, at the size on your order."
            )
          );
          card.appendChild(pf);
        }
        renderActions(item, card);
      }

      return card;
    }

    // Canon full view (delta ruling 9, 2026-08-24): name + meta header,
    // the return promise, and a labelled "Close full view" control.
    function openZoom(item, hasPrintFile) {
      var overlay = el("div", "pa-zoom");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-label", "Full view");

      var head = el("div", "pa-zoom__head");
      head.appendChild(
        el("span", "pa-zoom__name", item.petName || "Your pet")
      );
      var metaLine = isCombined(item)
        ? "One artwork · " + styleLabel(item.style)
        : styleLabel(item.style);
      if (metaLine) head.appendChild(el("span", "pa-zoom__meta", metaLine));
      overlay.appendChild(head);

      var body = el("div", "pa-zoom__body");
      var img = el("img", "pa-zoom__img");
      img.src = shownUrl(item);
      img.alt = (item.petName || "Pet") + " portrait, enlarged";
      body.appendChild(img);
      overlay.appendChild(body);

      var foot = el("div", "pa-zoom__foot");
      foot.appendChild(
        el(
          "p",
          "pa-zoom__note",
          // D20 fix pass 1 B4 (verbatim, resolves D26-COPY-CLAIM / h2):
          // one caption; the zoom shows the ≤2048px display render, so the
          // "same file that prints" claim is gone.
          "The artwork that prints, up close, so you can check every detail. Closing brings you back to exactly where you were."
        )
      );
      var close = el("button", "pa-zoom__closebtn", "Close full view");
      close.type = "button";
      function tearDown() {
        overlay.remove();
        document.removeEventListener("keydown", onKey);
      }
      function onKey(e) {
        if (e.key === "Escape") tearDown();
      }
      close.addEventListener("click", tearDown);
      body.addEventListener("click", function (e) {
        if (e.target === body) tearDown();
      });
      document.addEventListener("keydown", onKey);
      foot.appendChild(close);
      overlay.appendChild(foot);
      document.body.appendChild(overlay);
      close.focus();
    }

    // ------------------------------------------------------------------
    // Pending: approve + guided request-changes
    // ------------------------------------------------------------------
    function renderActions(item, card) {
      var actions = el("div", "pa-actions");
      var mem = itemMemorial(item);

      var approveBtn = el("button", "pa-btn pa-btn--primary", "Approve for Print");
      approveBtn.type = "button";
      var feedbackBtn = el("button", "pa-btn pa-btn--quiet", "Request Changes");
      feedbackBtn.type = "button";

      // Canon written approve-failure (delta ruling 6, 2026-08-24): a
      // tappable card that names what is true — nothing was sent, the
      // piece is exactly as before. Never auto-retried past the button.
      function showApproveFailure() {
        var existing = actions.querySelector(".pa-fail");
        if (existing) existing.remove();
        var failBtn = el("button", "pa-fail");
        failBtn.type = "button";
        failBtn.appendChild(
          el("strong", "pa-fail__head", "Couldn't reach us — tap to try again")
        );
        var failBody;
        if (mem) {
          // Memorial register (guardian pass 2026-08-24): the pet is
          // never described as waiting. The portrait is unchanged.
          failBody = isCombined(item)
            ? "This portrait is unchanged, exactly as before. Nothing was sent."
            : (item.petName || "Your pet") +
              "'s portrait is unchanged, exactly as before. Nothing was sent.";
        } else {
          var subject = isCombined(item)
            ? "This portrait"
            : item.petName || "Your pet";
          failBody =
            subject +
            " is still waiting on you, exactly as before. Nothing was sent.";
        }
        failBtn.appendChild(el("span", "pa-fail__body", failBody));
        failBtn.addEventListener("click", function () {
          failBtn.remove();
          approveBtn.click();
        });
        // Artifact order: button row · failure card · helper line.
        var rowNode = actions.querySelector(".pa-actions__row");
        actions.insertBefore(failBtn, rowNode ? rowNode.nextSibling : null);
      }

      approveBtn.addEventListener("click", function () {
        approveBtn.disabled = true;
        feedbackBtn.disabled = true;
        approveBtn.textContent = "Approving…";
        post("/approve", { orderItemId: item.id })
          .then(function () {
            item.approvalStatus = "approved";
            if (!item.approvedAt) item.approvedAt = new Date().toISOString();
            if (!item.approvalSource) item.approvalSource = "customer";
            track("artwork_approve", { style: item.style });
            startBeat(item);
            render();
            loadConsent();
            autoAdvance(item);
          })
          .catch(function () {
            approveBtn.disabled = false;
            feedbackBtn.disabled = false;
            approveBtn.textContent = "Approve for Print";
            showApproveFailure();
          });
      });

      // D13 motion grammar: the two buttons share one row (side by side
      // from 750 up, stacked at 390) that rises into place when the
      // spinner gives way; the helper line follows on the stagger.
      var row = el("div", "pa-actions__row");
      row.appendChild(approveBtn);
      feedbackBtn.addEventListener("click", function () {
        openFeedbackSheet(item, card);
      });
      row.appendChild(feedbackBtn);
      actions.appendChild(row);

      // Canon consequence copy (delta ruling 2, 2026-08-24): approval is
      // one-way, said before the tap rather than after. The revision
      // promise keeps the ruled terms-folded form ("about 2 working
      // days" — delta ruling 5).
      var termsPet = item.petName || "your pet";
      var pullTarget = isCombined(item)
        ? "this portrait"
        : termsPet + "'s file";
      // Step 31 (D20-B item 9, artifact pullLine): a digital order names
      // the file, never a printing pull.
      var pullLine = state.digitalOnly
        ? "When you approve, the full-size file is finished from exactly this proof, and it can't be changed after that."
        : "When you approve, an artist pulls " +
          pullTarget +
          " for printing the next working day, and it can't be changed after that.";
      actions.appendChild(
        el(
          "p",
          "pa-terms",
          pullLine +
            " Revisions are free, and a change usually takes about 2 working days."
        )
      );

      card.appendChild(actions);
    }

    // Canon feedback sheet (delta ruling 8, 2026-08-24): structured
    // prompts, the customer's own words, and the composed note shown
    // before it is sent. The engine still receives one string.
    function buildFeedbackForm(item) {
      var form = el("div", "pa-form");
      var mem = itemMemorial(item);
      var petLabel = item.petName || "your pet";

      form.appendChild(
        el("h3", "pa-sheet__head", "Tell the artist what to look at")
      );
      form.appendChild(
        el(
          "p",
          "pa-sheet__sub",
          "You know " +
            petLabel +
            " better than anyone. Point at what feels off and the artist works from there."
        )
      );

      form.appendChild(el("p", "pa-sheet__label", "WHERE SHOULD THEY LOOK"));
      var chipRow = el("div", "pa-chips");
      var chipDefs = [
        ["Likeness", "the likeness"],
        ["Colors", "the colors"],
        ["Crop", "the crop"],
        ["Background", "the background"],
        ["Something else", "something else"],
      ];
      var picked = {};
      chipDefs.forEach(function (def) {
        var label = def[0];
        var chip = el("button", "pa-chip", label);
        chip.type = "button";
        chip.setAttribute("aria-pressed", "false");
        chip.addEventListener("click", function () {
          picked[label] = !picked[label];
          chip.setAttribute("aria-pressed", String(Boolean(picked[label])));
          refresh();
        });
        chipRow.appendChild(chip);
      });
      form.appendChild(chipRow);

      form.appendChild(el("p", "pa-sheet__label", "IN YOUR WORDS"));
      var textarea = el("textarea", "pa-textarea");
      textarea.placeholder = mem
        ? "She held one ear down when she was happy…"
        : "She holds one ear down when she is happy…";
      textarea.maxLength = 2000;
      textarea.addEventListener("input", function () {
        refresh();
      });
      form.appendChild(textarea);

      var hintRow = el("div", "pa-sheet__hintrow");
      hintRow.appendChild(
        el(
          "span",
          "pa-sheet__hint",
          "Small details help most. The artist reads every word."
        )
      );
      var count = el("span", "pa-sheet__count", "0 / 2000");
      hintRow.appendChild(count);
      form.appendChild(hintRow);

      var previewBox = el("div", "pa-fact");
      previewBox.appendChild(
        el("strong", "pa-fact__title", "What the artist receives")
      );
      var previewEmpty =
        "Pick a place to look, or write a line, and the note the artist reads appears here.";
      var previewText = el("span", "pa-sheet__preview", previewEmpty);
      previewBox.appendChild(previewText);
      form.appendChild(previewBox);

      form.appendChild(
        el(
          "p",
          "pa-sheet__terms",
          "It goes to the artist on this piece. Revisions are free and updated proofs usually come back within 2 working days. " +
            (mem
              ? "Nothing prints until you approve."
              : "Nothing prints while your changes are in progress.")
        )
      );

      function composed() {
        var phrases = chipDefs
          .filter(function (def) {
            return picked[def[0]];
          })
          .map(function (def) {
            return def[1];
          });
        var lead = "";
        if (phrases.length === 1) {
          lead = "Looking at " + phrases[0] + ".";
        } else if (phrases.length > 1) {
          lead =
            "Looking at " +
            phrases.slice(0, -1).join(", ") +
            " and " +
            phrases[phrases.length - 1] +
            ".";
        }
        var note = textarea.value.trim();
        return [lead, note]
          .filter(function (part) {
            return Boolean(part);
          })
          .join(" ");
      }

      function refresh() {
        count.textContent = textarea.value.length + " / 2000";
        var text = composed();
        previewText.textContent = text || previewEmpty;
        if (sendBtn.textContent !== "Sending…") {
          sendBtn.disabled = text.length === 0;
        }
      }

      // Canon written send-failure: the draft is kept word for word and
      // only the customer retries — a duplicate note is worse than a
      // delay, so feedback is never auto-retried.
      function showSendFailure() {
        var existing = form.querySelector(".pa-failnote");
        if (existing) existing.remove();
        var note = el("div", "pa-failnote");
        note.appendChild(
          el("strong", "pa-fact__title", "Couldn't send that just now")
        );
        note.appendChild(
          document.createTextNode(
            "Your note is still here, word for word. Tap Send again when you are ready — we will not send it behind your back."
          )
        );
        form.insertBefore(note, sendBtn);
      }

      var sendBtn = el(
        "button",
        "pa-btn pa-btn--primary",
        "Send this to the artist"
      );
      sendBtn.type = "button";
      sendBtn.disabled = true;
      sendBtn.addEventListener("click", function () {
        var text = composed();
        if (!text || sendBtn.disabled) return;
        var pendingFail = form.querySelector(".pa-failnote");
        if (pendingFail) pendingFail.remove();
        sendBtn.disabled = true;
        sendBtn.textContent = "Sending…";
        post("/feedback", { orderItemId: item.id, feedback: text })
          .then(function () {
            item.approvalStatus = "changes_requested";
            item.customerFeedback = text;
            // Same pattern as approve: stamp locally so the status line
            // reads today's date before the next GET (the API writes
            // feedback_at in the same UPDATE once migration 016 is live).
            if (!item.feedbackAt) item.feedbackAt = new Date().toISOString();
            state.sentFeedback[item.id] = text;
            track("artwork_feedback", { style: item.style });
            render();
          })
          .catch(function (err) {
            sendBtn.textContent = "Send this to the artist";
            sendBtn.disabled = false;
            if (err && err.status === 409) {
              showError(form, "This artwork is already approved.");
            } else {
              showSendFailure();
            }
          });
      });
      form.appendChild(sendBtn);

      var backBtn = el("button", "pa-sheet__back", "Back to the proof");
      backBtn.type = "button";
      backBtn.addEventListener("click", function () {
        form.remove();
        // A refetch that arrived while the sheet was open paints now.
        flushDeferredRender();
      });
      form.appendChild(backBtn);

      return form;
    }

    // Opens the feedback sheet under the actions. D13 rider: the caret
    // lands in the textarea on a fine pointer only — on a coarse pointer
    // a programmatic focus summons the keyboard over the sheet the
    // customer just opened. (4.75 focused the textarea BEFORE the form
    // was in the document, so it never took effect on any pointer.)
    function openFeedbackSheet(item, card) {
      if (card.querySelector(".pa-form")) return;
      var form = buildFeedbackForm(item);
      card.appendChild(form);
      if (!coarsePointer()) {
        var ta = form.querySelector(".pa-textarea");
        if (ta) ta.focus({ preventScroll: true });
      }
    }

    // Step 31 — the partial-order beat (artifact doApprove): after a fresh
    // approve on an order that still needs the customer, the piece shows
    // one short beat ("{Name} ✓ · 1 to go") and then advances. The
    // memorial branch has no tick, no count and no auto-advance — the next
    // piece is there when she wants it. Never on a single-piece order.
    function startBeat(item) {
      clearBeat();
      var remaining = unresolvedItems().filter(function (i) {
        return i.id !== item.id;
      }).length;
      if (visibleItems().length < 2 || remaining === 0) return;
      state.beat = {
        id: item.id,
        name: item.petName || "This piece",
        remaining: remaining,
        memorial: itemMemorial(item),
      };
    }

    function clearBeat() {
      clearTimeout(state.beatTimer);
      state.beatTimer = null;
      state.beat = null;
    }

    function renderBeat(item, card) {
      var beat = state.beat;
      var wrap = el("div", "pa-approved pa-approved--piece pa-beat");
      wrap.appendChild(
        el(
          "h3",
          "pa-approved__head pa-approved__head--piece",
          beat.memorial
            ? possessive(beat.name) + " portrait is approved"
            : beat.name + " ✓ · " + beat.remaining + " to go"
        )
      );
      wrap.appendChild(
        el(
          "p",
          "pa-approved__sub",
          beat.memorial
            ? "It goes to print exactly as you saw it. The next piece is here when you want it."
            : "That one goes to print as you saw it. Taking you to the next piece now."
        )
      );
      var go = el("button", "pa-btn pa-btn--outline", "Go to the next piece");
      go.type = "button";
      go.addEventListener("click", function () {
        advanceFromBeat(item);
      });
      wrap.appendChild(go);
      card.appendChild(wrap);
    }

    function advanceFromBeat(fromItem) {
      clearBeat();
      var visible = visibleItems();
      for (var i = 0; i < visible.length; i++) {
        if (
          visible[i].id !== fromItem.id &&
          visible[i].approvalStatus === "pending" &&
          shownUrl(visible[i])
        ) {
          state.focus = i;
          break;
        }
      }
      render();
    }

    function autoAdvance(fromItem) {
      // Per-artwork ceremony (approve-all is RESOLVED NO): confirmation
      // beat, then auto-advance to the next undecided artwork. The
      // memorial beat never advances on its own (step 31).
      if (!state.beat || state.beat.id !== fromItem.id) return;
      if (state.beat.memorial) return;
      var delay = reducedMotion() ? 0 : 1400;
      state.beatTimer = setTimeout(function () {
        if (!state.beat || state.beat.id !== fromItem.id) return;
        advanceFromBeat(fromItem);
      }, delay);
    }

    // ------------------------------------------------------------------
    // Approved state
    // ------------------------------------------------------------------
    // The status row (D13): one bold lead, then the facts joined by " · ".
    // "Approved September 4 · ships in 1–3 business days after the artwork is
    // approved · tracking comes by email". Lead runs undated when the
    // timestamp is missing — never a placeholder date.
    function statusRow(lead, rest) {
      var p = el("p", "pa-status");
      p.appendChild(el("strong", "pa-status__lead", lead));
      // Step 29: the memorial shipped line is the lead alone.
      if (rest.length) {
        p.appendChild(el("span", "pa-status__rest", " · " + rest.join(" · ")));
      }
      return p;
    }

    // Canon approved state (delta ruling 3, 2026-08-24; D13 return-visit
    // pass 2026-09-08): display headline + sub + ONE dated status line +
    // the four-fact stack. Memorial takes the plain register — statement
    // form, no celebration, no share, no consent.
    //
    // The full state renders only when every visible piece is approved.
    // On a partly resolved order the focused approved piece gets the
    // artifact's short piece-approved beat instead (renderPieceApproved),
    // so the ship line is never asserted against an order that still
    // needs the customer.
    function renderApprovedState(item, card) {
      var mem = itemMemorial(item);
      var name = item.petName || "Your pet";
      var visible = visibleItems();
      var multi = visible.length > 1;

      // Step 29 (D20 fix pass 2 D1): a SHIPPED piece takes the full state
      // once every piece is settled — approved, or changes requested (the
      // changes sibling gets its own quiet box below). Otherwise the live
      // rule stands: the full state waits until every piece is approved.
      var shipped = isShipped(item);
      var digital = state.digitalOnly;
      var completed = isCompleted(item);
      // Step 54: the shipped state counts a shipped changes_requested piece
      // with the pieces it went out with; `approved` (what the share row
      // and the file module read) stays the approved pieces only. The two
      // are the same list on every order without such a piece.
      var approved = approvedPool();
      var pool = shipped ? shippedStatePool() : approved;
      if (multi && !(shipped ? shippedFullState() : allVisibleApproved())) {
        renderPieceApproved(item, card);
        return;
      }

      var headline;
      if (completed) {
        // D20-B item 4: settled by fulfilment on a digital order.
        headline = possessive(name) + " portrait is complete";
      } else if (shipped) {
        // D20-B item 8: a combined piece reads as the group it is.
        headline = isCombined(item)
          ? memberCount(item) === 2
            ? "The portrait of both has shipped"
            : "The portrait of all " +
              (FILE_WORD[memberCount(item)] || memberCount(item)) +
              " has shipped"
          : mem
            ? possessive(name) + " portrait has shipped"
            : pool.length === 2
              ? "Both pieces have shipped"
              : pool.length > 2
                ? "All " + pool.length + " pieces have shipped"
                : possessive(name) + " portrait has shipped";
      } else if (digital) {
        headline =
          pool.length === 2
            ? "Both portraits are approved"
            : pool.length > 2
              ? "All " + (FILE_WORD[pool.length] || pool.length) + " portraits are approved"
              : possessive(name) + " portrait is approved";
      } else if (mem) {
        headline = possessive(name) + " portrait is approved";
      } else if (multi) {
        // Canon (artifact approvedHeadline): two pieces read "Both".
        headline =
          visible.length === 2
            ? "Both pieces are going to print"
            : "All " + visible.length + " pieces are going to print";
      } else if (isCombined(item)) {
        headline = name + " are going to print";
      } else {
        headline = name + " is going to print";
      }

      var wrap = el("div", "pa-approved");
      wrap.appendChild(el("h3", "pa-approved__head", headline));
      // Memorial sub: the artifact draws "We will take care of her
      // portrait." for Sadie. The theme holds no gender for a pet, so the
      // pet's name stands where the pronoun was — the same sentence shape,
      // no invented pronoun.
      // Subs per the artifact's approvedSub (fix pass 1 B1: no verdict
      // word; the shipped sub says it once, the fact stack carries the
      // person).
      wrap.appendChild(
        el(
          "p",
          "pa-approved__sub",
          completed
            ? "Nothing ships on a digital order. The file below is the order."
            : shipped
              ? pool.length > 1 && !isCombined(item)
                ? "They were printed and sent."
                : "It was printed and sent."
              : digital
                ? pool.length > 1
                  ? "The files are yours, below."
                  : "The file is yours, below."
                : mem
                  ? "It goes to print exactly as you saw it. We will take care of " +
                    possessive(name) +
                    " portrait."
                  : "Here is what happens on our side."
        )
      );

      // The dated status line — the only new datum on the page. approvedAt
      // is in the payload since 4.75; on a multi-piece order the latest
      // approval dates the order. Undated if the field is missing.
      var approvedAt = null;
      visible.forEach(function (i) {
        if (i.approvedAt && (!approvedAt || i.approvedAt > approvedAt)) {
          approvedAt = i.approvedAt;
        }
      });
      var dateLabel = fmtDay(approvedAt);
      if (completed) {
        // D20-B item 4: "Completed {fulfilledAt} · nothing ships · your file
        // is below". Undated lead if the fulfilment carries no timestamp.
        var doneDay = fmtDay(item.fulfilledAt);
        wrap.appendChild(
          statusRow(doneDay ? "Completed " + doneDay : "Completed", [
            "nothing ships",
            "your file is below",
          ])
        );
      } else if (shipped) {
        // D1 status: "Shipped {fulfilledAt} · tracking came by email", plus
        // "you approved {it|Name} {date}" ONLY where she approved this
        // piece herself. approvedAt is null on shipped approvals, so no
        // approval date is ever printed for them. Memorial: the ship date.
        var shippedAt = null;
        pool.forEach(function (i) {
          if (i.fulfilledAt && (!shippedAt || i.fulfilledAt > shippedAt)) {
            shippedAt = i.fulfilledAt;
          }
        });
        var shipDay = fmtDay(shippedAt);
        var shipRest = [];
        if (!mem) {
          shipRest.push("tracking came by email");
          var ownDay = approvedByCustomer(item) ? fmtDay(item.approvedAt) : "";
          if (ownDay) {
            shipRest.push(
              "you approved " + (pool.length > 1 ? name : "it") + " " + ownDay
            );
          }
        }
        wrap.appendChild(
          statusRow(shipDay ? "Shipped " + shipDay : "Shipped", shipRest)
        );
      } else if (digital) {
        // D3 status. Undrawn when the piece carries no approval date (a
        // digital order settled by fulfilment): the line is withheld rather
        // than printing an undated "Approved" (step 29 gap, reported).
        if (dateLabel) {
          wrap.appendChild(
            statusRow("Approved " + dateLabel, [
              "nothing ships",
              pool.length > 1 ? "your files are below" : "your file is below",
            ])
          );
        }
      } else {
        wrap.appendChild(
          statusRow(dateLabel ? "Approved " + dateLabel : "Approved", [
            "ships in 1–3 business days",
            "tracking comes by email",
          ])
        );
      }

      if (shipped) {
        var openSib = renderSiblingOpen();
        if (openSib) wrap.appendChild(openSib);
      }

      wrap.appendChild(
        renderNextFacts(item, mem, name, shipped, digital, completed, pool)
      );
      card.appendChild(wrap);

      // Step 54: the share row and the file module are offered on an
      // APPROVED piece only, as before; a shipped changes_requested piece
      // gets neither (the proof on screen is the one she asked to change).
      if (item.approvalStatus !== "approved") return;

      // D2 (ruled): a memorial piece never carries share, in any order — the
      // share row belongs to the living pet, even while the rail is on the
      // memorial tile. Kept on shipped pieces (D1: it claims no approval).
      var sharePet = null;
      if (!mem) {
        sharePet = item;
      } else {
        for (var s = 0; s < approved.length; s++) {
          if (!itemMemorial(approved[s])) {
            sharePet = approved[s];
            break;
          }
        }
      }
      if (sharePet) card.appendChild(renderShareBlock(sharePet));

      // D20: the print-ready file module, after the share row and before
      // consent (which render() appends strictly last).
      var fileBlock = renderFileModule(item, approved);
      if (fileBlock) card.appendChild(fileBlock);
    }

    // D1, shipped with a changes_requested sibling: the quiet box. The
    // sibling's file is never offered while its artwork is open (the file
    // module lists approved pieces only). Step 54: a sibling that shipped
    // is not open ("doesn't print until you approve it" would be false), so
    // it draws no box.
    function renderSiblingOpen() {
      var sib = null;
      visibleItems().forEach(function (i) {
        if (!sib && i.approvalStatus === "changes_requested" && !isShipped(i)) {
          sib = i;
        }
      });
      if (!sib) return null;
      var sibName = sib.petName || "This piece";
      var box = el("div", "pa-fact pa-sibling");
      box.appendChild(el("strong", "pa-fact__title", sibName + " is still open"));
      box.appendChild(
        el(
          "span",
          "pa-sibling__body",
          "Your changes are with the team, and the revised proof comes by email. " +
            sibName +
            " doesn’t print until you approve it."
        )
      );
      var go = el("button", "pa-file__link", "Look at " + possessive(sibName) + " request");
      go.type = "button";
      go.addEventListener("click", function () {
        var idx = visibleItems().indexOf(sib);
        if (idx > -1) state.focus = idx;
        render();
      });
      box.appendChild(go);
      return box;
    }

    // ------------------------------------------------------------------
    // D20 — the print-ready file (canon fileVals, strings verbatim)
    // ------------------------------------------------------------------
    var FILE_WORD = ["", "one", "two", "three", "four", "five"];
    var FILE_STAYS =
      "It stays on this page, so you can download it again any time.";

    function fileMoney(n) {
      if (!(n > 0)) return "";
      return "$" + (n % 1 === 0 ? String(n) : n.toFixed(2));
    }

    // A combined artwork's pet count, from its joined roster "A, B & C".
    function memberCount(item) {
      return String(item.petName || "")
        .split(/\s*(?:,|&|\band\b)\s*/)
        .filter(Boolean).length;
    }

    function fileSubject(item) {
      if (!isCombined(item)) return possessive(item.petName || "Your pet") + " piece";
      var n = memberCount(item);
      return n === 2
        ? "The portrait of both"
        : "The portrait of all " + (FILE_WORD[n] || n);
    }

    function fileOfName(item) {
      return isCombined(item)
        ? "The print-ready file"
        : possessive(item.petName || "Your pet") + " print-ready file";
    }

    // Artifact `bought`: how she came to own it.
    function boughtLine(d) {
      if (d.source === "checkout") return "You added it at checkout.";
      if (d.source === "order") return "It came with your order.";
      var day = fmtDay(d.boughtAt);
      return day ? "You bought it on " + day + "." : "";
    }

    // Artifact kind() (D20-B fix pass 1): included (memorial) · pending (a
    // checkout opened) · owned / ownPrep / ownFailed (hers; ready, being
    // prepared, or the master did not write) · failed (print file being
    // rebuilt) · offer — and null where the canon draws no row: the offer
    // switch off, or the master not measured (the API sends no checkoutUrl).
    function fileKind(item) {
      var d = fileFacts(item);
      if (!d) return null;
      if (itemMemorial(item)) return "included";
      if (d.pending) return "pending";
      if (d.owned) {
        if (d.status === "failed") return "ownFailed";
        if (d.status === "ready" && d.downloadUrl) return "owned";
        return "ownPrep";
      }
      if (item.hiresStatus === "failed") return "failed";
      if (d.checkoutUrl) return "offer";
      return null;
    }

    // "the full-size file we print from" — but on a digital order nothing
    // is printed (W6): "the full-size file".
    function fullToken() {
      return state.digitalOnly
        ? "the full-size file"
        : "the full-size file we print from";
    }

    function fileBox(eyebrow) {
      var box = el("div", "pa-file");
      if (eyebrow) box.appendChild(el("p", "pa-file__eyebrow", eyebrow));
      // R2(a) / artifact fileTopPad: 30px when the module follows the share
      // card on an order with a memorial piece.
      if (memorialInPool() && sharePetOf(approvedPool())) {
        box.classList.add("pa-file--after-share");
      }
      return box;
    }

    // The living pet whose share card renders, or null (D2).
    function sharePetOf(pool) {
      for (var i = 0; i < pool.length; i++) {
        if (!itemMemorial(pool[i])) return pool[i];
      }
      return null;
    }

    // "$15 · a separate checkout." (fix pass 1 B5), price in the weight.
    function priceLine(label) {
      var p = el("p", "pa-file__body");
      p.appendChild(el("strong", "pa-file__price", label));
      p.appendChild(document.createTextNode(" · a separate checkout."));
      return p;
    }

    // The delivery: a plain link to the API's owner-only endpoint, which
    // re-checks ownership and redirects to a short-lived signed download.
    function downloadLink(cls, label, item, d) {
      var a = el("a", cls, label);
      a.href = apiUrl + d.downloadUrl;
      a.rel = "nofollow";
      a.addEventListener("click", function () {
        // Port Step 49: file_source, never `source` (GA4 reads that as the
        // session's traffic source; perkie-analytics.js header note).
        track("print_file_download", {
          pet_index: item.petNumber,
          included: d.included ? 1 : 0,
          file_source: d.source || "included",
        });
      });
      return a;
    }

    function trackFileShown(item, kind) {
      var key = item.id + ":" + kind;
      if (state.fileShownTracked[key]) return;
      state.fileShownTracked[key] = true;
      track("print_file_shown", { pet_index: item.petNumber, kind: kind });
    }

    // The API stamps "checkout started" (digitalFile.pending) and returns
    // the cart permalink; the page only navigates. On failure a single
    // piece falls back to its own permalink; otherwise the page re-reads
    // server truth (no failure copy is drawn for this control).
    function startCheckout(btn, items) {
      if (btn.disabled) return;
      btn.disabled = true;
      track("print_file_checkout_start", {
        pieces: items.length,
        pet_index: items.map(function (i) { return i.petNumber; }).join(","),
      });
      post("/digital-file/checkout", {
        orderItemIds: items.map(function (i) { return i.id; }),
      })
        .then(function (json) {
          if (!json || !json.checkoutUrl) throw new Error("no checkoutUrl");
          window.location.assign(json.checkoutUrl);
        })
        .catch(function (err) {
          btn.disabled = false;
          var d = items.length === 1 ? fileFacts(items[0]) : null;
          if (d && d.checkoutUrl && !(err && err.status === 409)) {
            window.location.assign(d.checkoutUrl);
            return;
          }
          // Step 31 (D20-B item 6): on the multi module a failed start
          // draws the tappable failure card; a 409 means server truth moved
          // (owned, pending, switch off) and the page re-reads it instead.
          if (items.length > 1 && !(err && err.status === 409)) {
            state.checkoutFailed = items.map(function (i) { return i.id; });
            track("print_file_checkout_failed", { pieces: items.length });
            renderUnlessSheetOpen();
            return;
          }
          load();
        });
    }

    // Which module the approved state carries (artifact fileVals). One
    // approved piece → the single states; more → the list. No facts (pre-
    // step-29 API) → nothing, exactly as before.
    function renderFileModule(item, pool) {
      var rebuild = item.hiresStatus === "failed";
      if (pool.length > 1) {
        // artifact fileOn: never on a tile whose print file is being rebuilt
        return rebuild ? null : renderFileMulti(pool);
      }
      var d = fileFacts(item);
      if (!d) return null;
      if (itemMemorial(item)) return renderFileMemorial(item, d);
      if (d.owned) {
        return d.status === "ready" && d.downloadUrl && !rebuild
          ? renderFileOwned(item, d)
          : renderFilePreparing(item, d);
      }
      if (d.pending) return renderFilePreparing(item, d);
      // Step 31 (D20-B item 7): the offer renders whenever the API sends a
      // checkout link; the size line waits for maxPrintSize (the master
      // may still be building).
      if (!rebuild && d.checkoutUrl) return renderFileOffer(item, d);
      return null;
    }

    // Single piece, on offer (kill switch on, API-gated).
    function renderFileOffer(item, d) {
      trackFileShown(item, "offer");
      var box = fileBox("THE PRINT-READY FILE");
      box.appendChild(el("p", "pa-file__head", fileSubject(item) + ", to print wherever you like"));
      box.appendChild(
        el(
          "p",
          "pa-file__body",
          "It’s the full-size file we print from. The copy above is sized for your phone, and this one’s for paper, for your wall or someone else’s. Print it anywhere, as often as you want."
        )
      );
      if (d.maxPrintSize) {
        box.appendChild(el("p", "pa-file__body", "Prints sharp up to " + d.maxPrintSize + "."));
      }
      box.appendChild(priceLine(fileMoney(Number(d.price))));
      var btn = el("button", "pa-btn pa-btn--quiet pa-btn--file", "Get the print-ready file");
      btn.type = "button";
      btn.addEventListener("click", function () {
        startCheckout(btn, [item]);
      });
      box.appendChild(btn);
      box.appendChild(
        el(
          "p",
          "pa-file__foot",
          "Once it’s yours, the download is right here. " + FILE_STAYS
        )
      );
      return box;
    }

    // Single piece, owned and ready: the download IS the delivery.
    function renderFileOwned(item, d) {
      trackFileShown(item, "owned");
      var box = fileBox("THE PRINT-READY FILE");
      box.appendChild(el("p", "pa-file__head", fileOfName(item) + " is yours"));
      box.appendChild(
        el(
          "p",
          "pa-file__body",
          [
            boughtLine(d),
            "It’s " + fullToken() + ", so you can print it anywhere, as often as you want.",
            FILE_STAYS,
          ].filter(Boolean).join(" ")
        )
      );
      box.appendChild(
        downloadLink("pa-btn pa-btn--quiet pa-btn--file", "Download the print-ready file", item, d)
      );
      // Measured: the size; unmeasured (artifact ownedUnmeasured): no number.
      box.appendChild(
        el(
          "p",
          "pa-file__foot",
          d.fileMb
            ? "PNG · about " + d.fileMb + " MB · easiest to save on a computer."
            : "PNG · easiest to save on a computer."
        )
      );
      return box;
    }

    // Single piece: bought and not ready yet, a checkout started (fix pass
    // 1 B7b — the page never re-offers what she just bought), or the file
    // failed to prepare (B7c). No button, no duration.
    function renderFilePreparing(item, d) {
      var prepFailed = d.owned && d.status === "failed";
      trackFileShown(item, prepFailed ? "prep_failed" : d.owned ? "preparing" : "pending");
      var box = fileBox("THE PRINT-READY FILE");
      var pendingOnly = !d.owned && d.pending;
      box.appendChild(
        el(
          "p",
          "pa-file__head",
          fileOfName(item) +
            (prepFailed
              ? " needs another try on our side"
              : pendingOnly
                ? " is waiting on your checkout"
                : " is being prepared")
        )
      );
      // D20-B fix pass 1: the prep-failed body names the alert that exists
      // (one deduped Slack alert per failed item, API 8d19a09); the
      // checkout-opened body drops "has started" (guardian W).
      var full = fullToken();
      var body;
      if (prepFailed) {
        body = [
          boughtLine(d),
          "The " +
            full.slice(4) +
            " didn’t finish preparing for download. A person on our team has been alerted, and the download appears here once it’s ready. If you’d like to ask about it, reply to your order email.",
        ];
      } else if (pendingOnly) {
        body = [
          "You opened a checkout for it. Once that’s finished, the download appears right here.",
          FILE_STAYS,
        ];
      } else {
        body = [
          boughtLine(d),
          "It’s " + full + ", and the download appears here once it’s ready.",
          FILE_STAYS,
        ];
      }
      box.appendChild(el("p", "pa-file__body", body.filter(Boolean).join(" ")));
      return box;
    }

    // Memorial (fix pass 1 C2): the file is free and simply hers. One
    // quiet line and one download — no eyebrow, no price, no conversion
    // language, no duration while it is prepared.
    function renderFileMemorial(item, d) {
      var name = item.petName || "Your pet";
      var ready = d.status === "ready" && Boolean(d.downloadUrl);
      var box = fileBox(null);
      box.classList.add("pa-file--memorial");
      // D20-B item 5 (memorial, file failed): the alert is true (API
      // 8d19a09) — no button, no duration, nothing asked of her.
      box.appendChild(
        el(
          "p",
          "pa-file__body",
          ready
            ? possessive(name) + " portrait file is here, full size, whenever you want it."
            : d.status === "failed"
              ? possessive(name) +
                " portrait file isn’t ready yet. A person on our team has been told, and it will be here, on this page."
              : possessive(name) + " portrait file is being prepared. It will be here, on this page."
        )
      );
      if (ready) {
        box.appendChild(
          downloadLink(
            "pa-btn pa-btn--quiet pa-btn--file",
            "Download " + possessive(name) + " portrait file",
            item,
            d
          )
        );
      }
      return box;
    }

    // More than one approved piece (fix pass 1 B6 + C2; D20-B items 1, 2,
    // 6): ONE module, every listed pet in it. `listed` = the non-memorial
    // pieces the canon draws a row for (offer / owned / being prepared /
    // checkout opened / print file being rebuilt); a piece with nothing to
    // say (offer off, master unmeasured) is dropped, never invented. The
    // button counts only what is for sale; with nothing to sell the module
    // still stands for the owned files ("Both files are yours"). The
    // memorial piece is its own "Included" row after the list; when it is
    // the only thing left, the memorial-only form renders (R2(a)).
    function renderFileMulti(pool) {
      var listed = pool.filter(function (it) {
        return !itemMemorial(it) && fileKind(it) !== null;
      });
      var kinds = listed.map(fileKind);
      var memPet = memorialInPool();
      var nl = listed.length;
      if (nl === 0) {
        var memFacts = memPet ? fileFacts(memPet) : null;
        return memFacts ? renderFileMemorial(memPet, memFacts) : null;
      }
      var buyable = listed.filter(function (it, i) { return kinds[i] === "offer"; });
      var nb = buyable.length;
      var taken = kinds.some(function (k) {
        return k === "owned" || k === "ownPrep" || k === "ownFailed" || k === "pending";
      });
      var each = nb ? Number(fileFacts(buyable[0]).price) : 0;
      var allLabel =
        nb === 1
          ? "Get " + possessive(buyable[0].petName || "Your pet") + " file"
          : !taken
            ? nb === 2 ? "Get both files" : "Get all " + (FILE_WORD[nb] || nb) + " files"
            : nb === 2 ? "Get the other two" : "Get the other " + (FILE_WORD[nb] || nb);
      buyable.forEach(function (it) { trackFileShown(it, "offer"); });
      var full = fullToken();

      var box = fileBox(nb === 0 && nl === 1 ? "THE PRINT-READY FILE" : "THE PRINT-READY FILES");
      box.appendChild(
        el(
          "p",
          "pa-file__head",
          nb >= 1
            ? "Each piece, to print wherever you like"
            : nl === 1
              ? possessive(listed[0].petName || "Your pet") + " file is yours"
              : nl === 2
                ? "Both files are yours"
                : "All " + (FILE_WORD[nl] || nl) + " files are yours"
        )
      );
      box.appendChild(
        el(
          "p",
          "pa-file__body",
          nb >= 1
            ? "Each one is " +
              full +
              ". The copies above are sized for your phone, and these are for paper, for your wall or someone else’s. Print them anywhere, as often as you want."
            : nl === 1
              ? "It’s " + full + ", so you can print it anywhere, as often as you want."
              : "Each one is " + full + ", so you can print them anywhere, as often as you want."
        )
      );
      if (nb >= 1) {
        // Fix pass 1 B3: "each" only when two or more rows are buyable.
        box.appendChild(priceLine(nb === 1 ? fileMoney(each) : fileMoney(each) + " each"));
        var all = el("button", "pa-btn pa-btn--quiet pa-btn--file", allLabel + " · " + fileMoney(each * nb));
        all.type = "button";
        all.addEventListener("click", function () {
          state.checkoutFailed = null;
          startCheckout(all, buyable);
        });
        box.appendChild(all);
        // D20-B item 6: the failed checkout start, a tappable card that
        // retries the same set. Cleared by the next attempt or refetch.
        if (state.checkoutFailed) {
          var retryIds = state.checkoutFailed;
          var retrySet = buyable.filter(function (it) {
            return retryIds.indexOf(it.id) > -1;
          });
          if (retrySet.length === 0) retrySet = buyable;
          var failCard = el("button", "pa-fail pa-fail--checkout");
          failCard.type = "button";
          failCard.appendChild(
            el("strong", "pa-fail__head", "The checkout didn’t open. Tap to try again.")
          );
          failCard.appendChild(
            el(
              "span",
              "pa-fail__body",
              "Nothing was charged, and every file below is still here to get."
            )
          );
          failCard.addEventListener("click", function () {
            state.checkoutFailed = null;
            startCheckout(failCard, retrySet);
          });
          box.appendChild(failCard);
        }
      }

      var rows = el("div", "pa-file__rows");
      listed.forEach(function (it, i) {
        var row = renderFileRow(it, kinds[i], nb, each);
        if (row) rows.appendChild(row);
      });
      box.appendChild(rows);
      box.appendChild(
        el(
          "p",
          "pa-file__foot",
          nb >= 1
            ? "Once they’re yours, the downloads are right here. They stay on this page, so you can download them again any time."
            : nl === 1
              ? FILE_STAYS
              : "They stay on this page, so you can download them again any time."
        )
      );

      // The memorial "Included" row (artifact memRow), after the list.
      if (memPet) {
        var md = fileFacts(memPet);
        var mrow = el("div", "pa-file__row pa-file__row--memorial");
        var mtext = el("span", "pa-file__rowtext");
        mtext.appendChild(el("span", "pa-file__rowname", memPet.petName || "Your pet"));
        var mready = Boolean(md && md.status === "ready" && md.downloadUrl);
        mtext.appendChild(
          el(
            "span",
            "pa-file__rowsub",
            md && md.status === "failed"
              ? "Included · a person on our team has been told, and it will be here"
              : !mready
                ? "Included · being prepared, and it will be here"
                : "Included · full size, whenever you want it"
          )
        );
        mrow.appendChild(mtext);
        if (mready) mrow.appendChild(downloadLink("pa-file__link", "Download", memPet, md));
        box.appendChild(mrow);
      }
      return box;
    }

    // One list row (artifact fileRows + SUB). The sub is withheld only
    // where the canon draws a datum the API has not sent (an offer row
    // whose master is still being measured).
    function renderFileRow(it, kind, nb, each) {
      if (!kind) return null;
      var d = fileFacts(it);
      var row = el("div", "pa-file__row");
      var text = el("span", "pa-file__rowtext");
      text.appendChild(el("span", "pa-file__rowname", it.petName || "Your pet"));
      var sub = "";
      if (kind === "offer") sub = d.maxPrintSize ? "Prints sharp up to " + d.maxPrintSize : "";
      else if (kind === "owned") sub = d.fileMb ? "Yours · PNG · about " + d.fileMb + " MB" : "Yours · PNG";
      else if (kind === "ownPrep") sub = "Yours · the download appears here once it’s ready";
      else if (kind === "ownFailed") sub = "Yours · a person on our team has been alerted, and the download appears here once it’s ready";
      else if (kind === "pending") sub = "Checkout opened · the download appears here once it’s finished";
      else if (kind === "failed") sub = "Being rebuilt by an artist. It’s offered here once that’s done.";
      if (sub) text.appendChild(el("span", "pa-file__rowsub", sub));
      row.appendChild(text);
      if (kind === "offer" && nb > 1) {
        var just = el(
          "button",
          "pa-file__link",
          "Just " + possessive(it.petName || "Your pet") + " · " + fileMoney(each)
        );
        just.type = "button";
        just.addEventListener("click", function () {
          startCheckout(just, [it]);
        });
        row.appendChild(just);
      }
      if ((kind === "owned" || kind === "included") && d.downloadUrl) {
        row.appendChild(downloadLink("pa-file__link", "Download", it, d));
      }
      return row;
    }

    // D13 nextFacts, verbatim from the artifact's renderVals(). Fact 2 is
    // head-only: the ruled time sentence is the head, and a body that
    // restated a different duration in working days was ruled drift at
    // delegation — the body is dropped, not reworded.
    function renderNextFacts(item, mem, name, shipped, digital, completed, pool) {
      var stack = el("div", "pa-nextfacts");
      if (shipped || digital || completed) {
        return renderSettledFacts(stack, mem, shipped, completed, item, name, pool);
      }
      var printSubject = mem
        ? possessive(name) + " portrait"
        : isCombined(item)
          ? "the piece"
          : name;
      // Fix pass 1 B1: no process claim ("by hand" is embargoed).
      stack.appendChild(
        nextFact(
          "A person reads your approval",
          "Then a person moves " + printSubject + " to print."
        )
      );
      stack.appendChild(
        nextFact("Ships in 1–3 business days", "")
      );
      if (mem) {
        // Deb: "wrong" covers the likeness, by name.
        stack.appendChild(
          nextFact(
            "Every piece is checked before it is packed",
            "If it doesn’t look like " +
              name +
              " when it arrives, or anything else is wrong, tell us and we will make it right."
          )
        );
      } else {
        var qualityFact = nextFact(
          "Every piece is checked before it is packed",
          "Nothing leaves here without a person looking at it first."
        );
        var guarantee = el("span", "pa-guarantee");
        guarantee.appendChild(
          el("span", "pa-guarantee__label", "Love It Guaranteed")
        );
        guarantee.appendChild(
          el("span", "pa-guarantee__body", "Revision, reprint, or refund.")
        );
        qualityFact.appendChild(guarantee);
        stack.appendChild(qualityFact);
      }
      // Fix pass 1: only the tracking email is a verified claim.
      stack.appendChild(
        nextFact(
          "Tracking arrives by email",
          mem
            ? "Nothing else is needed from you."
            : "You won’t need to check back. We email you tracking when it ships."
        )
      );
      return stack;
    }

    // Step 29 — D20 fix pass 2 + step 31 fix pass 1, verbatim from the
    // artifact's nextFacts: D1 shipped (past tense; "checked by a person"
    // said once; the likeness remedy by name; the guarantee kept, it still
    // holds after delivery), B5 memorial shipped (the remedy is its own
    // fact, then tracking), completed (digital, settled by fulfilment) and
    // D3 digital-only (no shipping, packing, tracking or guarantee line —
    // the guarantee names a reprint).
    function renderSettledFacts(stack, mem, shipped, completed, item, name, pool) {
      if (shipped && mem) {
        stack.appendChild(
          nextFact(
            "If it doesn’t look like " + name,
            "When it arrives, or if anything else is wrong, reply to your order email. We will make it right."
          )
        );
        stack.appendChild(
          nextFact(
            "Tracking went to your email",
            "If you can’t find it, reply to your order email and we’ll help."
          )
        );
        return stack;
      }
      if (shipped) {
        // Artifact likeName: the living pets on the order, or "them" for a
        // combined piece — every name from the payload.
        var likeName = isCombined(item)
          ? "them"
          : (pool || [])
              .filter(function (i) { return !itemMemorial(i); })
              .map(function (i) { return i.petName || "your pet"; })
              .join(" or ") || name;
        var checked = nextFact(
          "Checked by a person before it was packed",
          "If it doesn’t look like " +
            likeName +
            ", or anything else is wrong, reply to your order email."
        );
        var guarantee = el("span", "pa-guarantee");
        guarantee.appendChild(
          el("span", "pa-guarantee__label", "Love It Guaranteed")
        );
        guarantee.appendChild(
          el("span", "pa-guarantee__body", "Revision, reprint, or refund.")
        );
        checked.appendChild(guarantee);
        stack.appendChild(checked);
        stack.appendChild(
          nextFact(
            "Tracking went to your email",
            "If you can’t find it, reply to your order email and we’ll help."
          )
        );
        return stack;
      }
      if (completed) {
        stack.appendChild(
          nextFact(
            "If it doesn’t look like " + name,
            "Reply to your order email and a person will help."
          )
        );
        return stack;
      }
      stack.appendChild(
        nextFact(
          "Nothing ships on this order",
          "It’s a digital order, so there’s nothing to pack or send. " +
            (pool && pool.length > 1
              ? "The files below are the order."
              : "The file below is the order.")
        )
      );
      stack.appendChild(
        nextFact("Questions go to your order email", "Reply to it and a person will help.")
      );
      return stack;
    }

    // Artifact `showPieceApproved`: one piece settled on an order that
    // still needs the customer. Short, no ship line, a route to the next
    // open piece. Auto-advance (autoAdvance) still runs after a fresh tap.
    function renderPieceApproved(item, card) {
      var name = item.petName || "This piece";
      var wrap = el("div", "pa-approved pa-approved--piece");
      wrap.appendChild(
        el(
          "h3",
          "pa-approved__head pa-approved__head--piece",
          name + (isShipped(item) ? " has shipped" : " is approved for print")
        )
      );
      // D1: a shipped piece beside a still-open proof names what is true.
      var openProof = null;
      visibleItems().forEach(function (i) {
        if (!openProof && i !== item && i.approvalStatus === "pending") openProof = i;
      });
      wrap.appendChild(
        el(
          "p",
          "pa-approved__sub",
          // D20-B fix pass 1 (guardian): no "judged", no "settled".
          isShipped(item)
            ? name +
                " has shipped. " +
                (openProof
                  ? possessive(openProof.petName || "The next piece") +
                    " proof is still open, and it waits for you."
                  : "")
            : "This one can’t be changed now. The rest of the order still needs you."
        )
      );
      var next = nextOpenItem(item);
      if (next) {
        var go = el(
          "button",
          "pa-btn pa-btn--outline",
          "Look at " + (next.petName || "the next piece") + " next"
        );
        go.type = "button";
        go.addEventListener("click", function () {
          var idx = visibleItems().indexOf(next);
          if (idx > -1) state.focus = idx;
          render();
        });
        wrap.appendChild(go);
      }
      card.appendChild(wrap);
    }

    function nextFact(head, body) {
      var row = el("div", "pa-nextfacts__row");
      row.appendChild(el("strong", "pa-nextfacts__head", head));
      if (body) row.appendChild(el("span", "pa-nextfacts__body", body));
      return row;
    }

    function renderShareBlock(item) {
      var wrap = el("div", "pa-share");
      // Step 31 canon heading (D29-CANON-UNPORTED, artifact shareHead): on
      // an order with a memorial piece the card is named for the living pet.
      wrap.appendChild(
        el(
          "p",
          "pa-share__head",
          memorialOrder() && item.petName
            ? "A card of " + item.petName + " for your phone, if you want one"
            : "A card for your phone, if you want one"
        )
      );
      var row = el("div", "pa-share__row");

      var shareBtn = el(
        "button",
        "pa-btn pa-btn--primary",
        "Share " + (item.petName || "the") + "'s portrait"
      );
      if (!item.petName) shareBtn.textContent = "Share this portrait";
      shareBtn.type = "button";
      shareBtn.addEventListener("click", function () {
        makeShareCard(item)
          .then(function (blob) {
            var file = new File([blob], "perkie-print.png", {
              type: "image/png",
            });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              return navigator.share({ files: [file] });
            }
            saveBlob(blob, "perkie-print.png");
          })
          .catch(function () {
            // Canvas blocked (CORS) or share dismissed — fall back to the
            // plain image in a new tab; never an error state for sharing.
            window.open(shownUrl(item), "_blank", "noopener");
          });
      });
      row.appendChild(shareBtn);

      var dlBtn = el("button", "pa-btn pa-btn--quiet", "Save a copy for your phone");
      dlBtn.type = "button";
      dlBtn.addEventListener("click", function () {
        var url = item.previewUrl || shownUrl(item);
        fetch(url)
          .then(function (res) {
            if (!res.ok) throw new Error("fetch failed");
            return res.blob();
          })
          .then(function (blob) {
            var name =
              (item.petName || "perkie").toLowerCase().replace(/[^a-z0-9]+/g, "-") +
              "-perkie-print" + (blob.type === "image/webp" ? ".webp" : ".png");
            saveBlob(blob, name);
          })
          .catch(function () {
            window.open(url, "_blank", "noopener");
          });
      });
      row.appendChild(dlBtn);
      wrap.appendChild(row);
      wrap.appendChild(
        el(
          "p",
          "pa-share__caption",
          "Your finished piece, whole — nothing cropped."
        )
      );
      return wrap;
    }

    function saveBlob(blob, name) {
      var a = document.createElement("a");
      var url = URL.createObjectURL(blob);
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 5000);
    }

    /** 9:16 share artifact (Flow 4 grammar): 1080x1920, paper ground,
        artwork centered, pet name + wordmark line. Non-memorial only. */
    function makeShareCard(item) {
      return new Promise(function (resolve, reject) {
        var src = new Image();
        src.crossOrigin = "anonymous";
        src.onload = function () {
          try {
            var canvas = document.createElement("canvas");
            canvas.width = 1080;
            canvas.height = 1920;
            var ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF"; // --perkie-paper
            ctx.fillRect(0, 0, 1080, 1920);

            var maxW = 920;
            var maxH = 1200;
            var scale = Math.min(maxW / src.width, maxH / src.height);
            var w = src.width * scale;
            var h = src.height * scale;
            var x = (1080 - w) / 2;
            var y = (1920 - h) / 2 - 80;
            ctx.save();
            ctx.shadowColor = "rgba(56,56,56,0.18)";
            ctx.shadowBlur = 30;
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(x - 24, y - 24, w + 48, h + 48);
            ctx.restore();
            ctx.drawImage(src, x, y, w, h);

            ctx.fillStyle = "#383838"; // --perkie-ink
            ctx.textAlign = "center";
            ctx.font = "800 64px Archivo, sans-serif";
            if (item.petName) {
              ctx.fillText(item.petName, 540, y + h + 140);
            }
            ctx.fillStyle = "#6B6B60";
            ctx.font = "500 36px Archivo, sans-serif";
            ctx.fillText("A Perkie Print", 540, y + h + 200);

            canvas.toBlob(function (blob) {
              if (blob) resolve(blob);
              else reject(new Error("toBlob failed"));
            }, "image/png");
          } catch (e) {
            reject(e);
          }
        };
        src.onerror = function () {
          reject(new Error("image load failed"));
        };
        src.src = shownUrl(item);
      });
    }

    // ------------------------------------------------------------------
    // Changes-requested state
    // ------------------------------------------------------------------
    // D13 (2026-09-08): written for the return visit. What was sent, when
    // (feedbackAt — undated when the API has no timestamp; requests made
    // before migration 016 carry none and there is no backfill), that it
    // is with the team, that the update comes by email, and that nothing
    // prints until the customer approves. changes_requested is terminal in
    // the API: no revised proof ever appears on this page, so nothing here
    // implies one will. No SMS or nudge claim (no sender exists), and no
    // clock-restart sentence — these orders are exempt from the
    // auto-approve window and no clock restarts.
    function renderChangesState(item, card) {
      var mem = itemMemorial(item);
      var name = item.petName || "your pet";
      var pieceRef = isCombined(item) ? "this piece" : name + "'s piece";

      var wrap = el("div", "pa-changes");
      wrap.appendChild(
        el("h3", "pa-changes__head", "Your request is with the team")
      );
      wrap.appendChild(
        el(
          "p",
          "pa-approved__sub",
          mem
            ? "The revised proof comes to you by email."
            : "The revised proof comes to you by email, so there is nothing you need to do here."
        )
      );

      // Feature-detect the key: the field ships in perkie-api migration
      // 016. Absent, null, or unparseable → the line runs undated.
      var feedbackLabel = fmtDay(item.feedbackAt);
      var lead = feedbackLabel
        ? "Changes requested " + feedbackLabel
        : "Changes requested";
      wrap.appendChild(
        statusRow(
          lead,
          mem
            ? ["the update comes by email", "nothing prints until you approve"]
            : [
                "with the team",
                "the update comes by email",
                "nothing prints until you approve",
              ]
        )
      );

      var echoText =
        state.sentFeedback[item.id] ||
        item.customerFeedback ||
        // Guardian (step 18): the artifact's fallback named a note and an
        // artist; with no note on file, only the request is a fact.
        "Your request is on file with the team.";
      var echo = el("div", "pa-echo");
      echo.appendChild(el("span", "pa-sheet__label", "WHAT YOU SENT"));
      echo.appendChild(el("p", "pa-echo__note", echoText));
      wrap.appendChild(echo);

      var facts = el("div", "pa-nextfacts");
      facts.appendChild(
        nextFact(
          "Your request is open on this order",
          "It is with the team working on " + pieceRef + "."
        )
      );
      facts.appendChild(
        nextFact(
          "The update comes by email",
          "The revised proof arrives in your inbox rather than on this page, so there is nothing to come back and check."
        )
      );
      facts.appendChild(
        nextFact(
          "Nothing prints until you approve",
          mem ? "There is no time limit." : "Printing waits for your approval."
        )
      );
      wrap.appendChild(facts);

      // Wait block: the reply-to-order-email route and nothing else.
      var wait = el("div", "pa-fact pa-changes__wait");
      wait.appendChild(el("strong", "pa-fact__title", "While you wait"));
      wait.appendChild(
        document.createTextNode(
          "If you remember one more thing, reply to your order email and it reaches the team working on " +
            pieceRef +
            "."
        )
      );
      wrap.appendChild(wait);

      card.appendChild(wrap);
    }

    function showError(container, message) {
      var existing = container.querySelector(".pa-error");
      if (existing) existing.remove();
      container.appendChild(el("p", "pa-error", message));
    }

    // ------------------------------------------------------------------
    // Page-level fact cards (quality / delivery / deadline)
    // ------------------------------------------------------------------
    function renderFacts(anyPending, mem, focused) {
      var facts = el("div", "pa-facts");

      // Quality fact card — the canonized guarantee (ledger 2026-08-22).
      // Skipped when the focused piece is approved: the canon fact stack
      // (delta ruling 3) already carries the guarantee there, and the
      // same promise must never render twice on one screen. Skipped for
      // changes requested too (D13): the artifact's changes view carries
      // its own three facts and the wait block, and nothing below them.
      var focusedStatus = focused ? focused.approvalStatus : "";
      // Step 31 (D20-B item 9): a digital order packs and ships nothing, so
      // the packing line is withheld there (the artifact draws no such
      // card at all; the digital register carries no print framing).
      if (
        focusedStatus !== "approved" &&
        focusedStatus !== "changes_requested" &&
        !state.digitalOnly
      ) {
        var quality = el("div", "pa-fact");
        quality.appendChild(
          document.createTextNode(
            "Every piece is checked before it is packed. Love It Guaranteed — revision, reprint, or refund."
          )
        );
        facts.appendChild(quality);
      }

      if (anyPending) {
        // Delivery line: approval-anchored, never a date (2026-08-20
        // ruling). Never on a digital order (nothing ships).
        if (!state.digitalOnly) {
          facts.appendChild(
            elFact("Your order ships in 1–3 business days.")
          );
        }

        if (mem) {
          // Deb's on-page exemption — unconditional, verbatim (spec 15 §11).
          // R1 (ruled 2026-09-24): memorial pieces are exempt from the
          // day-7 runbook, so no deadline line ever renders here.
          facts.appendChild(elFact("There is no time limit on this one."));
        } else {
          // R1 (ruled 2026-09-24, artifact showDeadlineLine + autoLine): the
          // day-7 rule as a person's act, not a timer; digital-only names
          // the file. Physical and digital share the one rule.
          facts.appendChild(
            elFact(
              "Take your time. If we haven’t heard from you in 7 days, a person on our team approves it as you see it" +
                (state.digitalOnly
                  ? ", and the full-size file is finished from this proof"
                  : "") +
                ". Approving now, or asking for changes, is always yours."
            )
          );
        }
      }

      return facts;
    }

    function elFact(text) {
      var f = el("div", "pa-fact");
      f.appendChild(document.createTextNode(text));
      return f;
    }

    // ------------------------------------------------------------------
    // Consent block (spec 17 Surface A — the guest consent home).
    // Renders ONLY from the ask-state GET (never the items array); the
    // engine already omits memorial pets and combined artworks. Single
    // site-sample toggle at launch (ads consent HELD, ruling 2026-08-21).
    // ------------------------------------------------------------------
    function renderConsentArea() {
      if (!state.consent) return null;
      var enginePets = state.consent.pets || [];
      // D20 fix pass 2 D1: consent is DROPPED for any piece approved by
      // shipping — it asks to show work she signed off, and she never
      // signed this one off. Account and Consent still holds the setting.
      // (Memorial and combined are already omitted by the engine.) On an
      // order that shipped, the artifact's eligibility is exact: approved
      // by her, not memorial, not combined — an open sibling is not asked.
      var orderShipped = visibleItems().some(isShipped);
      var pets = enginePets.filter(function (p) {
        for (var i = 0; i < state.items.length; i++) {
          if (state.items[i].id === p.orderItemId) {
            var it = state.items[i];
            if (it.approvalSource === "shipped") return false;
            return !orderShipped || it.approvalStatus === "approved";
          }
        }
        return true;
      });
      if (enginePets.length > 0 && pets.length === 0) return null;

      if (pets.length === 0) {
        if (memorialOrder()) {
          // All-memorial order: affirmative privacy statement, never blank
          // (spec 17 rider; Deb's fix).
          var wrapM = el("div", "pa-consent");
          wrapM.appendChild(
            el(
              "p",
              "pa-consent__body",
              "This portrait is private. It is not shown on our site or anywhere else."
            )
          );
          return wrapM;
        }
        return null;
      }

      if (!state.consentShownTracked) {
        state.consentShownTracked = true;
        track("sample_consent_shown", { surface: "approval" });
      }

      var wrap = el("div", "pa-consent");
      var multi = pets.length > 1;
      var firstName = pets[0].petName || "your pet";

      // D13 approved RETURN state: the choice was stored at approval
      // (migration 012) and this GET reads it back. A pet is "answered"
      // when the engine has stamped sampleConsentAt (a grant stamps it; a
      // withdraw keeps it as audit) — default-off with no stamp is not an
      // answer. Answered → the eyebrow and the sub line say so instead of
      // re-asking; the rows below already render the saved state.
      var answered = pets.some(function (p) {
        return p.sampleConsent === true || Boolean(p.sampleConsentAt);
      });
      if (answered) {
        wrap.appendChild(
          el("p", "pa-sheet__label", "OPTIONAL · YOUR ANSWER IS SAVED")
        );
      }
      // Guardian F1 (2026-08-23): the multi heading NAMES the asked pets so
      // a memorial pet on a mixed order (which the engine omits from this
      // list by design) is never ambiguously swept into the ask.
      var names = pets.map(function (p) {
        return p.petName || "your pet";
      });
      var joined =
        names.length > 1
          ? names.slice(0, -1).join(", ") + " and " + names[names.length - 1]
          : names[0];
      // D20-B fix pass 1 B4: the question names the pet (the engine's list
      // already omits memorial and combined pieces, so on a mixed order the
      // non-memorial pet is the only subject). Two or more read as the
      // order — except on an order with a memorial piece, where guardian
      // F1 still holds: "the portraits in this order" would sweep the
      // memorial pet in, so the asked pets are named instead.
      wrap.appendChild(
        el(
          "h3",
          "pa-consent__heading",
          multi
            ? memorialOrder()
              ? "May we show " + possessive(joined) + " portraits to other pet parents?"
              : "May we show the portraits in this order to other pet parents?"
            : "May we show " + possessive(firstName) + " portrait to other pet parents?"
        )
      );

      // V-B / V-B2 with the ratified scope string (locked-string amendment,
      // ledger 2026-08-21: pet's name, never yours).
      var city = state.consent.displayCity || null;
      var bodyText;
      if (city) {
        bodyText =
          "We show a few real customer portraits on our site as examples. If you say yes, the portrait may appear with " +
          firstName +
          "'s name and your city, like \"" +
          firstName +
          " · " +
          city +
          "\". Your pet's name and your city only — never your name, never your address. Nothing is shared unless you say yes, and you can change your mind anytime: contact us and it comes down.";
      } else {
        bodyText =
          "We show a few real customer portraits on our site as examples. If you say yes, " +
          firstName +
          "'s portrait may appear with " +
          firstName +
          "'s name. Your pet's name only — never your name, never your address. Nothing is shared unless you say yes, and you can change your mind anytime: contact us and it comes down.";
      }
      // Return visit (D13): the terms were read when she answered; the sub
      // line now says the answer is kept and can change. The ratified
      // scope paragraph still renders for anyone who has not answered.
      if (answered) {
        bodyText =
          "You answered this when you approved. You can change this any time.";
      }
      wrap.appendChild(el("p", "pa-consent__body", bodyText));

      pets.forEach(function (pet) {
        wrap.appendChild(renderConsentRow(pet));
      });

      wrap.appendChild(
        el(
          "p",
          "pa-consent__fine",
          "Samples appear in the style galleries only. You can change your answer any time — this page keeps working from the link in your order email."
        )
      );
      return wrap;
    }

    function renderConsentRow(pet) {
      var row = el("div", "pa-consent__row");
      var name = pet.petName || "Your pet";
      var on = pet.sampleConsent === true;
      var busy = state.consentBusy[pet.orderItemId] === true;

      // Thumbnail: presentation only — eligibility comes strictly from the
      // ask-state list; the items array just supplies the picture.
      var match = null;
      for (var i = 0; i < state.items.length; i++) {
        if (state.items[i].id === pet.orderItemId) match = state.items[i];
      }
      var thumbUrl = match ? match.previewUrl || shownUrl(match) : "";
      if (thumbUrl) {
        var thumb = el("img", "pa-consent__thumb");
        thumb.src = thumbUrl;
        thumb.alt = "";
        thumb.loading = "lazy";
        row.appendChild(thumb);
      }

      var meta = el("div", "pa-consent__meta");
      var chip = el("div", "pa-consent__chip");
      chip.appendChild(el("span", "pa-consent__chip-name", name));
      var chipCity = pet.displayCity || pet.sampleDisplayCity;
      if (chipCity) {
        chip.appendChild(el("span", "pa-consent__chip-city", "· " + chipCity));
      }
      meta.appendChild(chip);

      var stateLine = el("p", "pa-consent__state");
      if (on) {
        stateLine.textContent =
          name +
          " may appear in our site examples. Changed your mind? Tap 'Stop showing " +
          name +
          "' anytime.";
      } else if (state.withdrawn[pet.orderItemId]) {
        stateLine.textContent =
          name +
          " won't be shown. Nothing else changes about your order. It comes down with the next site update.";
      } else if (pet.sampleConsentAt) {
        stateLine.textContent =
          "Said yes on " +
          fmtDate(pet.sampleConsentAt) +
          ", changed since — all set. " +
          name +
          " won't be shown.";
      } else {
        stateLine.textContent = "Off unless you say yes.";
      }
      meta.appendChild(stateLine);

      if (state.consentErrors[pet.orderItemId]) {
        meta.appendChild(
          el("p", "pa-consent__state", state.consentErrors[pet.orderItemId])
        );
      }
      row.appendChild(meta);

      var toggle = el(
        "button",
        "pa-consent__toggle",
        busy
          ? "Saving…"
          : on
            ? "Stop showing " + name
            : "Yes — show " + name + " on the site"
      );
      toggle.type = "button";
      toggle.setAttribute("role", "switch");
      toggle.setAttribute("aria-checked", String(on));
      if (busy) toggle.disabled = true;
      toggle.addEventListener("click", function () {
        if (state.consentBusy[pet.orderItemId]) return;
        writeConsent(pet, !on);
      });
      row.appendChild(toggle);
      return row;
    }

    function writeConsent(pet, next) {
      // Write contract (spec 17 §11 critic lock-blocker 2): locked Saving…
      // pending state, server-confirm, snap-back on failure or 429, render
      // server truth, no offline queue.
      state.consentBusy[pet.orderItemId] = true;
      delete state.consentErrors[pet.orderItemId];
      render();
      post("/sample-consent", { orderItemId: pet.orderItemId, consent: next })
        .then(function (res) {
          pet.sampleConsent = res.sampleConsent === true;
          pet.sampleConsentAt = res.sampleConsentAt || pet.sampleConsentAt;
          pet.displayCity = res.displayCity || pet.displayCity;
          if (res.sampleConsent === false) {
            state.withdrawn[pet.orderItemId] = true;
            track("sample_consent_withdrawn", { surface: "approval" });
          } else {
            delete state.withdrawn[pet.orderItemId];
            track("sample_consent_granted", { surface: "approval" });
          }
          delete state.consentBusy[pet.orderItemId];
          render();
        })
        .catch(function (err) {
          // Snap back — a consent switch never asserts an unconfirmed state.
          delete state.consentBusy[pet.orderItemId];
          var reason =
            err && err.status === 429
              ? "rate_limited"
              : err && err.status === 409
                ? "not_eligible"
                : "error";
          track("consent_write_failed", { surface: "approval", reason: reason });
          state.consentErrors[pet.orderItemId] =
            err && err.status === 429
              ? err.message
              : err && err.status === 409
                ? err.message
                : "That didn't save. Please try again.";
          render();
        });
    }

    function loadConsent() {
      if (!anyApproved()) return;
      fetch(endpoint("/sample-consent"))
        .then(function (res) {
          if (!res.ok) throw new Error("status " + res.status);
          return res.json();
        })
        .then(function (data) {
          var prev = state.consent ? JSON.stringify(state.consent.pets) : null;
          state.consent = data;
          var now = JSON.stringify(data.pets || []);
          if (prev !== null && prev !== now) {
            // Silent stale correction (spec 17 rider).
            track("consent_stale_detected", { surface: "approval" });
          }
          renderUnlessSheetOpen();
        })
        .catch(function () {
          // Fail closed: no block renders from a guess. The approval flow
          // itself is untouched by a consent read failure.
          if (!state.consent) state.consent = null;
        });
    }

    // ------------------------------------------------------------------
    // Load + poll
    // ------------------------------------------------------------------
    function maybePoll() {
      var generating = state.items.some(isGenerating);
      // Step 29: after the checkout → thank-you → back trip the piece reads
      // Preparing until the purchase webhook lands; keep reading server
      // truth (same cadence and cap) so the download appears on its own.
      var filePreparing = state.items.some(function (i) {
        var d = fileFacts(i);
        return (
          i.approvalStatus === "approved" && d &&
          (d.pending || ((d.owned || d.included) && d.status === "preparing"))
        );
      });
      var withinWindow = Date.now() - state.pollStart < POLL_MAX_MS;
      clearTimeout(state.pollTimer); // one poll chain, however load() was reached
      if ((generating || filePreparing) && withinWindow) {
        state.pollTimer = setTimeout(load, POLL_MS);
      }
    }

    function load() {
      fetchStatus()
        .then(function (data) {
          // Merge by id so an open sheet's item reference stays live
          // across a background refetch (its send patches the same
          // object the next render reads).
          var incoming = data.items || [];
          var byId = {};
          state.items.forEach(function (i) {
            byId[i.id] = i;
          });
          state.items = incoming.map(function (row) {
            var existing = byId[row.id];
            if (!existing) return row;
            Object.keys(row).forEach(function (k) {
              existing[k] = row[k];
            });
            return existing;
          });
          state.order = { orderNumber: data.orderNumber || "" };
          state.digitalOnly = data.digitalOnly === true;
          state.loadError = false;
          state.notFound = false;
          // Step 31: a refetch is server truth — the failed-checkout card
          // does not outlive it.
          state.checkoutFailed = null;
          // Step 29 return path: land on the piece the thank-you link named.
          if (landingPiece) {
            var landVisible = visibleItems();
            for (var li = 0; li < landVisible.length; li++) {
              if (landVisible[li].id === landingPiece) {
                state.focus = li;
                landingPiece = "";
                break;
              }
            }
          }
          // D13 rider: a refetch (visibilitychange / poll) never wipes an
          // open feedback sheet — the repaint waits for the sheet to close.
          renderUnlessSheetOpen();
          maybePoll();
          loadConsent();
        })
        .catch(function (err) {
          if (err && err.notFound) {
            // No record yet: retry through the webhook-lag window, then
            // show the designed state (page) or hide (order context).
            if (Date.now() - state.pollStart < NOT_FOUND_RETRY_MS) {
              state.pollTimer = setTimeout(load, POLL_MS);
            } else {
              state.notFound = true;
              render();
            }
            return;
          }
          // Network / server error: retry briefly, then the designed error
          // state — never a blank screen (spec 15 §11).
          if (Date.now() - state.pollStart < NOT_FOUND_RETRY_MS) {
            state.pollTimer = setTimeout(load, POLL_MS);
          } else {
            state.loadError = true;
            render();
          }
        });
    }

    // Refetch on visibility (spec 17 rider): the page can sit open for
    // days; returning to the tab re-reads server truth for both surfaces.
    // D13 rider: the refetch goes through renderUnlessSheetOpen(), so a
    // half-written note survives the tab switch that the OS keyboard or a
    // photo app causes on a phone.
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "visible" && !state.loadError) {
        state.pollStart = Date.now(); // a return re-opens the poll window
        load();
      }
    });

    // Step 29: coming BACK from the Shopify checkout with the browser's
    // back button restores this page from the bfcache, still showing the
    // offer. Re-read server truth so Preparing (digitalFile.pending) shows.
    window.addEventListener("pageshow", function (e) {
      if (e.persisted && !state.loadError) {
        state.pollStart = Date.now();
        load();
      }
    });

    load();
  }

  function boot() {
    document.querySelectorAll("[data-perkie-approval]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
